import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Card,
  Col,
  Button,
  Form,
  Row,
  Breadcrumb,
  Spinner,
  Modal,
  Table,
  Nav,
  OverlayTrigger,
  Tooltip,
  Accordion,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faFileInvoiceDollar,
  faFolderOpen,
  faArrowRight,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import IconButton from "components/common/iconButton";
import Divider from "components/common/divider";
import { useForm, FormProvider, Controller, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import paths from "routes/paths";
import { useTranslation } from "react-i18next";
import {
  fetchRoleAndClaims,
  handleLoadingAndAccess,
  fetchLogoSrc,
  fetchTemplatesByCustomer,
  getTranslatedSalutation
} from "../../assets/js/common";
import ProposalModal from "components/proposals/proposalModals/proposalModal";
import { generateGUID } from "assets/js/common";
import { useAppContext } from "../../providers/appProvider";
import "assets/css/site.css";
import Select from "react-select";
import TimeInput from "./timeInput";
import ReactDOM from "react-dom";
import { setHours, setMinutes } from "date-fns";
import { getDomainConfig } from "../../config/domainConfig";
import { InputGroup } from "react-bootstrap";
import { getNthWeekdayOfMonth, getWeekdayKey, getOccurrenceLabel, getOccurrenceCount, localTimeToUtcHHmm } from "../../utils/recurrenceHelpers";

// Must stay at module scope: declaring this inside EditProposal gives it a new
// identity on every parent render, so React remounts the modal (flicker + lost state).
const TaskModal = ({
  show,
  handleClose,
  onSaveSelected,
  setValue,
  proposalId,
  currentVersionId,
  defaultJobFrequency,
  defaultJobFrequencyCount,
  proposalStartDate,
  setTaskWithSubtasks,
  setEnrichedTasks,
  setSelectedSubtasksMap,
}) => {
  const { t, i18n } = useTranslation();
  const [taskOptions, setTaskOptions] = useState([]);
  const [taskId, setTaskId] = useState(null);
  const [selectedFrequency, setSelectedFrequency] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [oneTimeDate, setOneTimeDate] = useState(null);
  const MAX_DAILY_TIME_SLOTS = 6;
  const MAX_MONTHLY_OCCURRENCE_ROWS = 4;
  const MAX_YEARLY_OCCURRENCE_ROWS = 6;
  const getDefaultDailyTime = () => setHours(setMinutes(new Date(), 0), 8);

  const [repeatEvery, setRepeatEvery] = useState(1);
  const [dailyTimes, setDailyTimes] = useState([getDefaultDailyTime()]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [monthlyDay, setMonthlyDay] = useState(new Date().getDate());
  const [monthlyType, setMonthlyType] = useState("specific_day");
  const [monthlyOccurrences, setMonthlyOccurrences] = useState([{ occurrence: "1st", weekday: "monday" }]);
  const [yearlyDates, setYearlyDates] = useState([{ day: new Date().getDate(), month: new Date().getMonth() }]);
  const [isLoading, setIsLoading] = useState(false);
  const { control, reset, register } = useForm();
  const dayKeys = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const jobFreqToTaskFreq = {
    OneTime: "OneTime",
    PerDay: "Daily",
    PerWeek: "Weekly",
    PerMonth: "Monthly",
    PerYear: "Yearly",
  };

  const resetForm = (prefillFrequency = "", prefillRepeatEvery = 1) => {
    reset({
      taskId: null,
      frequency: prefillFrequency,
      weekdays: {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
      },
    });

    setTaskId(null);
    setSelectedFrequency(prefillFrequency || "");
    setRepeatEvery(prefillRepeatEvery || 1);
    setDailyTimes([getDefaultDailyTime()]);
    setSelectedDays([]);
    setOneTimeDate(proposalStartDate ? new Date(proposalStartDate) : new Date());
    setMonthlyDay(new Date().getDate());
    setMonthlyType("specific_day");
    setMonthlyOccurrences([{ occurrence: "1st", weekday: "monday" }]);
    setYearlyDates([{ day: new Date().getDate(), month: new Date().getMonth() }]);
  };

  const addDailyTimeSlot = () => {
    setDailyTimes((prev) => {
      const currentTimes = prev || [getDefaultDailyTime()];
      if (currentTimes.length >= MAX_DAILY_TIME_SLOTS) return prev;
      const lastTime = currentTimes[currentTimes.length - 1];
      const newTime = moment(lastTime).add(1, "hour").toDate();
      return [...currentTimes, newTime];
    });
  };

  const updateDailyTimeSlot = (index, newTime) => {
    setDailyTimes((prev) => {
      const currentTimes = [...(prev || [getDefaultDailyTime()])];
      currentTimes[index] = newTime;
      return currentTimes;
    });
  };

  const removeDailyTimeSlot = (index) => {
    setDailyTimes((prev) => {
      const currentTimes = prev || [getDefaultDailyTime()];
      if (currentTimes.length <= 1) return prev;
      return currentTimes.filter((_, i) => i !== index);
    });
  };

  const addMonthlyOccurrenceRow = () => {
    setMonthlyOccurrences((prev) =>
      prev.length >= MAX_MONTHLY_OCCURRENCE_ROWS
        ? prev
        : [...prev, { occurrence: "1st", weekday: "monday" }]
    );
  };

  const removeMonthlyOccurrenceRow = (idx) => {
    setMonthlyOccurrences((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev
    );
  };

  const addYearlyDateRow = () => {
    setYearlyDates((prev) =>
      (prev || []).length >= MAX_YEARLY_OCCURRENCE_ROWS
        ? prev
        : [...(prev || []), { day: 1, month: 0 }]
    );
  };

  const removeYearlyDateRow = (idx) => {
    setYearlyDates((prev) =>
      (prev || []).length > 1 ? prev.filter((_, i) => i !== idx) : prev
    );
  };

  const handleDayToggle = (dayKey) => {
    setSelectedDays((prev) =>
      prev.includes(dayKey) ? prev.filter((d) => d !== dayKey) : [...prev, dayKey]
    );
  };
  const handleCloseWithReset = () => {
    resetForm();
    handleClose();
  };

  useEffect(() => {
    const today = moment().format("dddd").toLowerCase();
    const occurrenceCount = Math.max(Number(repeatEvery) || 1, 1);

    switch (selectedFrequency) {
      case "OneTime":
        setSelectedDays([]);
        if (setValue) {
          dayKeys.forEach((day) => setValue(`weekdays.${day}`, false));
        }
        setOneTimeDate(proposalStartDate ? new Date(proposalStartDate) : new Date());
        break;
      case "Daily": {
        const count = Math.min(occurrenceCount, MAX_DAILY_TIME_SLOTS);
        const times = [getDefaultDailyTime()];
        for (let i = 1; i < count; i++) {
          times.push(moment(times[i - 1]).add(1, "hour").toDate());
        }
        setDailyTimes(times);

        const weekdaysOnly = dayKeys.filter((d) => d !== "sunday");
        setSelectedDays(weekdaysOnly);
        if (setValue) {
          dayKeys.forEach((day) => setValue(`weekdays.${day}`, weekdaysOnly.includes(day)));
        }
        break;
      }
      case "Weekly": {
        const count = Math.min(occurrenceCount, 7);
        const baseIdx = dayKeys.includes(today) ? dayKeys.indexOf(today) : 0;
        const spreadDays = [];
        for (let i = 0; i < count; i++) {
          const idx = (baseIdx + Math.round((i * 7) / count)) % 7;
          const key = dayKeys[idx];
          if (!spreadDays.includes(key)) spreadDays.push(key);
        }
        setSelectedDays(spreadDays);
        if (setValue) {
          dayKeys.forEach((day) =>
            setValue(`weekdays.${day}`, spreadDays.includes(day))
          );
        }
        break;
      }
      case "Monthly": {
        const refDate = proposalStartDate ? new Date(proposalStartDate) : new Date();
        setMonthlyDay(refDate.getDate());
        const { nth, weekday } = getNthWeekdayOfMonth(refDate);
        const weekdayKey = getWeekdayKey(weekday);

        if (occurrenceCount > 1) {
          const count = Math.min(occurrenceCount, MAX_MONTHLY_OCCURRENCE_ROWS);
          const occurrenceOrder = ["1st", "2nd", "3rd", "4th"];
          const baseIdx = occurrenceOrder.indexOf(getOccurrenceLabel(nth));
          const startIdx = baseIdx === -1 ? 0 : baseIdx;
          const rows = [];
          for (let i = 0; i < count; i++) {
            const idx = (startIdx + Math.round((i * 4) / count)) % 4;
            const occurrence = occurrenceOrder[idx];
            if (!rows.some((r) => r.occurrence === occurrence)) {
              rows.push({ occurrence, weekday: weekdayKey });
            }
          }
          setMonthlyOccurrences(rows);
          setMonthlyType("weekday_occurrence");
        } else {
          setMonthlyOccurrences([{ occurrence: getOccurrenceLabel(nth), weekday: weekdayKey }]);
          setMonthlyType("specific_day");
        }
        break;
      }
      case "Yearly": {
        const refDate = proposalStartDate ? new Date(proposalStartDate) : new Date();
        const count = Math.min(occurrenceCount, MAX_YEARLY_OCCURRENCE_ROWS);
        const baseMonth = refDate.getMonth();
        const day = refDate.getDate();
        const rows = [];
        for (let i = 0; i < count; i++) {
          const month = (baseMonth + Math.round((i * 12) / count)) % 12;
          if (!rows.some((r) => r.month === month)) {
            rows.push({ day, month });
          }
        }
        setYearlyDates(rows.length > 0 ? rows : [{ day, month: baseMonth }]);
        break;
      }
      default:
        setSelectedDays([]);
        if (setValue) {
          dayKeys.forEach((day) => setValue(`weekdays.${day}`, false));
        }
    }
  }, [selectedFrequency, setValue, repeatEvery]);

  const fetchTaskData = async () => {
    const customerId = localStorage.getItem("customerId");

    if (!customerId) {
      console.error("CustomerId is missing.");
      return;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/taskRoute/tasks`,
      {
        params: { customerId },
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
    );

    setTaskOptions(
      (response.data || []).map((task) => ({
        value: task.TaskId,
        label: task.TaskName,
      }))
    );
  };
  const getWeekdayLabel = (day) => {
    if (!day) return "";
    return t(`fullDaysName.${day}`);
  };

  const getMonthLabel = (mIdx) => {
    const monthName = moment.months()[mIdx];
    return monthName ? t(`months.${monthName.toLowerCase()}`) : "";
  };

  const getOrdinalSuffix = (day, lang) => {
    if (lang === "nl") return "e";
    if (lang === "hi") return "";
    if (day === 1 || day === 21 || day === 31) return "st";
    if (day === 2 || day === 22) return "nd";
    if (day === 3 || day === 23) return "rd";
    return "th";
  };

  const getRecurrenceSummary = () => {
    if (!startDate || !selectedFrequency) return null;

    const dayNames = selectedDays
      .map((day) => getWeekdayLabel(day))
      .join(" , ");

    switch (selectedFrequency) {
      case "OneTime": {
        const formattedDate = moment(oneTimeDate || startDate).format("Do MMMM YYYY");
        return t("recurrenceSummaryOnce", { date: formattedDate });
      }
      case "Daily": {
        const times = dailyTimes && dailyTimes.length > 0 ? dailyTimes : [getDefaultDailyTime()];
        const timesStr = times.map((t) => moment(t).format("HH:mm")).join(", ");
        if (times.length <= 1) {
          return t("recurrenceSummaryDailySingleAlt", { time: timesStr });
        }
        return t("recurrenceSummaryDailyMultiAlt", { count: times.length, times: timesStr });
      }
             case "Weekly": {
        if (!dayNames) return "";
        return t("recurrenceSummaryWeeklyAlt", { days: dayNames });
      }
      case "Monthly": {
        if (monthlyType === "specific_day") {
          const dayText = monthlyDay === "last" ? t("recurrenceSummaryMonthlyLastDay") : t("recurrenceSummaryMonthlyDayN", { day: monthlyDay });
          return t("recurrenceSummaryMonthlyDayText", { dayText });
        } else {
          const desc = monthlyOccurrences
            .map((o) => `${t(o.occurrence)} ${getWeekdayLabel(o.weekday)}`)
            .join(` ${t("listConjunction") || "and"} `);
          return t("recurrenceSummaryMonthlyOccurrence", { desc });
        }
      }
      case "Yearly": {
        const desc = (yearlyDates || [])
          .map((d) => {
            const monthsStr = getMonthLabel(d.month);
            if (d.day === "last") return t("recurrenceSummaryYearlyLastDay", { month: monthsStr });
            const suffix = getOrdinalSuffix(d.day, i18n.language);
            return t("recurrenceSummaryYearlyDate", { day: d.day, suffix, month: monthsStr });
          })
          .join(` ${t("listConjunction") || "and"} `);
        return t("recurrenceSummaryYearly", { desc });
      }
      default:
        return "";
    }
  };

  const isFormValid =
    taskId &&
    selectedFrequency &&
    ((selectedFrequency === "Daily" && dailyTimes && dailyTimes.length > 0) ||
      (selectedFrequency === "Weekly" && selectedDays.length > 0) ||
      (selectedFrequency === "Monthly" &&
        (monthlyType === "specific_day"
          ? Boolean(monthlyDay)
          : monthlyOccurrences.length > 0)) ||
      (selectedFrequency === "OneTime" && Boolean(oneTimeDate)) ||
      (selectedFrequency === "Yearly" && yearlyDates && yearlyDates.length > 0));

  const handleSaveSelection = async () => {
    setIsLoading(true);
    try {
      const advancedRecurrence = {};
      if (selectedFrequency === "Daily") {
        advancedRecurrence.times = (dailyTimes && dailyTimes.length > 0 ? dailyTimes : [getDefaultDailyTime()])
          .slice()
          .sort((a, b) => moment(a).valueOf() - moment(b).valueOf())
          .map((t) => localTimeToUtcHHmm(t));
      } else if (selectedFrequency === "Monthly") {
        advancedRecurrence.monthlyType = monthlyType;
        if (monthlyType === "specific_day") {
          advancedRecurrence.monthlyDay = monthlyDay;
        } else {
          advancedRecurrence.monthlyOccurrences = monthlyOccurrences;
        }
      } else if (selectedFrequency === "Yearly") {
        advancedRecurrence.dates = yearlyDates;
      } else if (selectedFrequency === "OneTime") {
        advancedRecurrence.date = oneTimeDate
          ? moment(oneTimeDate).format("YYYY-MM-DD")
          : null;
      }

      const fullTask = {
        TaskId: taskId,
        Frequency: selectedFrequency,
        RepeatEvery: selectedFrequency === "OneTime" ? 0 : (repeatEvery || 1),
        SelectedDays: selectedFrequency === "Weekly"
          ? selectedDays
          : (["Daily", "Monthly", "Yearly", "OneTime"].includes(selectedFrequency) ? advancedRecurrence : null),
        MonthlyDay: selectedFrequency === "Monthly" && monthlyType === "specific_day" ? monthlyDay : null,
        ProposalId: proposalId,
        ProposalVersionId: currentVersionId || null,
      };

      if (fullTask.SelectedDays && typeof fullTask.SelectedDays === 'object') {
        fullTask.SelectedDays = JSON.stringify(fullTask.SelectedDays);
      }

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/proposalRoute/${proposalId}/tasks/add`,
        fullTask, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );

      if (res.status === 201) {
        await enrichAndAppendNewTask(res.data);
        resetForm();
        handleClose();
      } else {
        console.error("Error saving task: unexpected status", res.status);
      }
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error(t("toast.taskSaveError"), {
        position: "top-right",
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (show) {
      fetchTaskData();
      const mappedFreq = jobFreqToTaskFreq[defaultJobFrequency] || null;
      resetForm(mappedFreq, defaultJobFrequencyCount || 1);
    }
  }, [show]);

  const enrichAndAppendNewTask = async (newTask) => {
    try {
      const taskDetailsRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/taskRoute/getTaskById/${newTask.TaskId}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );

      const subtasksRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/subtaskRoute/subtasks/${newTask.TaskId}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );

      const enrichedTask = {
        ...newTask,
        label: taskDetailsRes.data?.TaskName || "Unnamed Task",
        recurrence: {
          frequency: newTask.Frequency,
          repeatEvery: newTask.RepeatEvery,
          selectedDays: (() => {
            try {
              const raw = newTask.SelectedDays;
              if (Array.isArray(raw)) return raw;
              if (typeof raw === "string") {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) return parsed;
                if (typeof parsed === "object" && parsed !== null) {
                  return parsed.selectedDays || [];
                }
              }
              return [];
            } catch {
              return [];
            }
          })(),
          monthlyDay: newTask.MonthlyDay,
          advancedRecurrence: (() => {
            try {
              const raw = newTask.SelectedDays;
              if (typeof raw === "string") {
                const parsed = JSON.parse(raw);
                if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
                  return parsed;
                }
              }
            } catch {}
            return null;
          })()
        },
        Subtasks: subtasksRes.data || [],
      };

      // Append new task at the end
      setTaskWithSubtasks((prev) => [...prev, enrichedTask]);
      setEnrichedTasks((prev) => [...prev, enrichedTask]);
      setSelectedSubtasksMap((prev) => ({
        ...prev,
        [enrichedTask.ProposalTaskId]: new Set(
          (enrichedTask.Subtasks || []).map((s) => s.SubtaskId)
        ),
      }));
    } catch (error) {
      console.error("Error enriching new task:", error);
    }
  };

  return (
    <Modal show={show} onHide={handleCloseWithReset} centered dialogClassName="task-details-modal">
      <Modal.Header closeButton>
        <Modal.Title>{t("taskSingular")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label className="data-font-size fw-semi-bold">
              {t("task")} <span className="text-danger">*</span>
            </Form.Label>
            <Controller
              name="taskId"
              control={control}
              rules={{ required: t("taskRequired") }}
              render={({ field, fieldState: { error } }) => {
                const options = taskOptions.map((task) => ({
                  value: task.value,
                  label: task.label,
                }));

                return (
                  <>
                    <Select
                      key={i18n.language}
                      options={options}
                      isClearable
                      {...field}
                      value={
                        options.find(
                          (opt) => opt.value === field.value
                        ) || null
                      }
                      onChange={(selected) => {
                        const selectedValue = selected
                          ? selected.value
                          : "";
                        field.onChange(selectedValue);
                        setTaskId(selectedValue);
                      }}
                      onBlur={field.onBlur}
                      className={`custom-select-container ${error ? "is-invalid" : ""}`}
                      classNamePrefix="custom-select"
                      placeholder={t("select")}
                      noOptionsMessage={() => t("noOptionsAvailable")}
                    />
                    {error && (
                      <div className="invalid-feedback d-block">
                        {error.message}
                      </div>
                    )}
                  </>
                );
              }}
            />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label className="data-font-size fw-semi-bold">
              {t("frequency")} <span className="text-danger">*</span>
            </Form.Label>
            <Controller
              name="frequency"
              control={control}
              rules={{ required: t("frequencyRequired") }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Nav
                    variant="pills"
                    activeKey={selectedFrequency}
                    onSelect={(selectedKey) => {
                      field.onChange(selectedKey);
                      setSelectedFrequency(selectedKey);
                    }}
                    className="frequency-tabs-nav mb-1"
                  >
                    <Nav.Item>
                      <Nav.Link eventKey="OneTime">{t("onetime")}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="Daily">{t("daily")}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="Weekly">{t("weekly")}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="Monthly">{t("monthly")}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="Yearly">{t("yearly")}</Nav.Link>
                    </Nav.Item>
                  </Nav>
                  {error && !selectedFrequency && (
                    <div className="invalid-feedback d-block">
                      {error.message}
                    </div>
                  )}
                </>
              )}
            />
          </Form.Group>

          {selectedFrequency === "OneTime" && (
            <div className="recurrence-settings-card">
              <div className="recurrence-settings-header">
                <i className="far fa-clock" />
                <span>{t("recurrenceSettings")}</span>
              </div>
              <Form.Group className="mb-0">
                <Form.Label className="data-font-size fw-semi-bold">
                  {t("taskDate")} <span className="text-danger">*</span>
                </Form.Label>
                <div className="task-date-input-wrap">
                  <DatePicker
                    selected={oneTimeDate}
                    onChange={(selectedDate) => setOneTimeDate(selectedDate)}
                    dateFormat="dd-MM-yyyy"
                    className="form-control"
                    placeholderText="dd-mm-yyyy"
                  />
                </div>
              </Form.Group>
            </div>
          )}

          {selectedFrequency === "Daily" && (
            <div className="recurrence-settings-card">
              <div className="recurrence-settings-header">
                <i className="far fa-clock" />
                <span>{t("recurrenceSettings")}</span>
              </div>
              <Form.Group className="mb-0">
                <Form.Label className="data-font-size fw-semi-bold d-block">
                  {t("startsAt") || "Starts at"} <span className="text-danger">*</span>
                </Form.Label>
                <div className="d-flex flex-column gap-2 mb-2 align-items-start">
                  {(dailyTimes || [getDefaultDailyTime()]).map((time, idx) => (
                    <div key={idx} className="d-inline-flex align-items-center gap-2">
                      <div className="daily-time-input-wrap">
                        <DatePicker
                          selected={time}
                          onChange={(newTime) => updateDailyTimeSlot(idx, newTime)}
                          showTimeSelect
                          showTimeSelectOnly
                          timeIntervals={15}
                          timeCaption="Time"
                          dateFormat="HH:mm"
                          timeFormat="HH:mm"
                          placeholderText="HH:mm"
                          className="form-control"
                          popperContainer={({ children }) => ReactDOM.createPortal(children, document.body)}
                          popperProps={{ strategy: "fixed" }}
                          customInput={
                            <TimeInput className="form-control" />
                          }
                        />
                      </div>
                      {(dailyTimes || []).length > 1 && (
                        <button
                          type="button"
                          className="remove-day-btn"
                          onClick={() => removeDailyTimeSlot(idx)}
                          aria-label={t("remove") || "Remove"}
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {(dailyTimes || []).length < MAX_DAILY_TIME_SLOTS && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 mt-1 text-decoration-none add-day-link"
                    onClick={addDailyTimeSlot}
                  >
                    {t("addAnotherTime") || "+ Add another time"}
                  </Button>
                )}
              </Form.Group>
            </div>
          )}

          {selectedFrequency === "Weekly" && (
            <div className="recurrence-settings-card">
              <div className="recurrence-settings-header">
                <i className="far fa-clock" />
                <span>{t("recurrenceSettings")}</span>
              </div>
              <Form.Group className="mb-0">
                <Form.Label className="data-font-size fw-semi-bold">
                  {t("weekday")} <span className="text-danger">*</span>
                </Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {dayKeys.map((dayKey) => (
                    <Button
                      key={dayKey}
                      variant={
                        selectedDays.includes(dayKey)
                          ? "success"
                          : "outline-secondary"
                      }
                      size="sm"
                      className="weekday-btn"
                      onClick={() => handleDayToggle(dayKey)}
                    >
                      {t(`days.${dayKey}`)}
                    </Button>
                  ))}
                </div>
              </Form.Group>
            </div>
          )}

          {selectedFrequency === "Monthly" && (
            <div className="recurrence-settings-card">
              <div className="recurrence-settings-header">
                <i className="far fa-clock" />
                <span>{t("recurrenceSettings")}</span>
              </div>
              <Form.Group className="mb-3">
                <Form.Label className="data-font-size fw-semi-bold">
                  {t("repeatBy") || "Repeat By"} <span className="text-danger">*</span>
                </Form.Label>
                <Row className="g-2">
                  <Col xs={6}>
                    <div
                      className={`recurrence-radio-card ${monthlyType === "weekday_occurrence" ? "active" : ""}`}
                      onClick={() => setMonthlyType("weekday_occurrence")}
                    >
                      <Form.Check
                        type="radio"
                        name="monthlyTypeRadioModal"
                        checked={monthlyType === "weekday_occurrence"}
                        onChange={() => setMonthlyType("weekday_occurrence")}
                        className="mb-0"
                      />
                      <span>{t("dayOfWeek") || "Day of Week"}</span>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div
                      className={`recurrence-radio-card ${monthlyType === "specific_day" ? "active" : ""}`}
                      onClick={() => setMonthlyType("specific_day")}
                    >
                      <Form.Check
                        type="radio"
                        name="monthlyTypeRadioModal"
                        checked={monthlyType === "specific_day"}
                        onChange={() => setMonthlyType("specific_day")}
                        className="mb-0"
                      />
                      <span>{t("dayOfMonth") || "Day of Month"}</span>
                    </div>
                  </Col>
                </Row>
              </Form.Group>

              {monthlyType === "specific_day" ? (
                <Row className="g-3 mb-2">
                  <Col xs={12} sm={5}>
                    <Form.Group>
                      <Form.Label className="data-font-size fw-semi-bold">
                        {t("onDay") || "On Day"} <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        value={monthlyDay}
                        onChange={(e) => {
                          const val = e.target.value;
                          setMonthlyDay(val === "last" ? "last" : Number(val));
                        }}
                        className="recurrence-inline-select"
                      >
                        {[...Array(31).keys()].slice(1).map((num) => (
                          <option key={num} value={num}>{num}</option>
                        ))}
                        <option value="last">{t("lastDay") || "Last Day"}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              ) : (
                <Form.Group className="mb-2">
                  <Form.Label className="data-font-size fw-semi-bold">
                    {t("weekday")} <span className="text-danger">*</span>
                  </Form.Label>
                  {monthlyOccurrences.map((occ, idx) => (
                    <div key={idx} className="recurrence-occurrence-row">
                      <Form.Select
                        value={occ.occurrence}
                        onChange={(e) => {
                          const val = e.target.value;
                          setMonthlyOccurrences((prev) => {
                            const arr = [...prev];
                            arr[idx] = { ...arr[idx], occurrence: val };
                            return arr;
                          });
                        }}
                        className="w-auto"
                      >
                        <option value="1st">{t("1st") || "1st"}</option>
                        <option value="2nd">{t("2nd") || "2nd"}</option>
                        <option value="3rd">{t("3rd") || "3rd"}</option>
                        <option value="4th">{t("4th") || "4th"}</option>
                        <option value="last">{t("last") || "Last"}</option>
                      </Form.Select>

                      <div className="d-flex flex-wrap gap-1">
                        {dayKeys.map((dayKey) => (
                          <Button
                            key={dayKey}
                            variant={occ.weekday === dayKey ? "success" : "outline-secondary"}
                            size="sm"
                            className="weekday-btn"
                            onClick={() => {
                              setMonthlyOccurrences((prev) => {
                                const arr = [...prev];
                                arr[idx] = { ...arr[idx], weekday: dayKey };
                                return arr;
                              });
                            }}
                          >
                            {t(`days.${dayKey}`)}
                          </Button>
                        ))}
                      </div>
                      {monthlyOccurrences.length > 1 && (
                        <button
                          type="button"
                          className="remove-day-btn"
                          onClick={() => removeMonthlyOccurrenceRow(idx)}
                          aria-label={t("remove") || "Remove"}
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  ))}

                  {monthlyOccurrences.length < MAX_MONTHLY_OCCURRENCE_ROWS && (
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 mt-1 text-decoration-none add-day-link"
                      onClick={addMonthlyOccurrenceRow}
                    >
                      {t("addAnotherDay") || "+ Add another day"}
                    </Button>
                  )}
                </Form.Group>
              )}
            </div>
          )}

          {selectedFrequency === "Yearly" && (
            <div className="recurrence-settings-card">
              <div className="recurrence-settings-header">
                <i className="far fa-clock" />
                <span>{t("recurrenceSettings")}</span>
              </div>
              <Form.Group className="mb-2">
                <Form.Label className="data-font-size fw-semi-bold">
                  {t("onDay") || "On Day"} <span className="text-danger">*</span>
                </Form.Label>
                {(yearlyDates || []).map((entry, idx) => (
                  <div key={idx} className="recurrence-occurrence-row">
                    <Form.Select
                      value={entry.day}
                      onChange={(e) => {
                        const val = e.target.value;
                        setYearlyDates((prev) => {
                          const arr = [...prev];
                          arr[idx] = { ...arr[idx], day: val === "last" ? "last" : Number(val) };
                          return arr;
                        });
                      }}
                      className="recurrence-inline-select"
                    >
                      {[...Array(31).keys()].slice(1).map((num) => (
                        <option key={num} value={num}>{num}</option>
                      ))}
                      <option value="last">{t("lastDay") || "Last Day"}</option>
                    </Form.Select>
                    <span className="text-muted font-size-13">{t("of") || "of"}</span>
                    <Form.Select
                      value={entry.month}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        setYearlyDates((prev) => {
                          const arr = [...prev];
                          arr[idx] = { ...arr[idx], month: val };
                          return arr;
                        });
                      }}
                      className="recurrence-inline-select"
                    >
                      {moment.months().map((month, mIdx) => (
                        <option key={month} value={mIdx}>
                          {t(`shortMonths.${month.toLowerCase()}`)}
                        </option>
                      ))}
                    </Form.Select>
                    {(yearlyDates || []).length > 1 && (
                      <button
                        type="button"
                        className="remove-day-btn"
                        onClick={() => removeYearlyDateRow(idx)}
                        aria-label={t("remove") || "Remove"}
                      >
                        &times;
                      </button>
                    )}
                  </div>
                ))}

                {(yearlyDates || []).length < MAX_YEARLY_OCCURRENCE_ROWS && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 mt-1 text-decoration-none add-day-link"
                    onClick={addYearlyDateRow}
                  >
                    {t("addAnotherDay") || "+ Add another day"}
                  </Button>
                )}
              </Form.Group>
            </div>
          )}

              {getRecurrenceSummary() && (
                <div className="recurrence-summary-box mb-3">
                  <div className="recurrence-summary-icon">
                    <i className="fas fa-check-circle" />
                  </div>
                  <div className="recurrence-summary-content">
                    <div className="recurrence-summary-title">
                      {t("recurrenceSummary")}
                    </div>
                    <div className="recurrence-summary-text">
                      {getRecurrenceSummary()}
                    </div>
                  </div>
                </div>
              )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={handleCloseWithReset}>
          {t("close")}
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveSelection}
          disabled={!isFormValid || isLoading}
        >
          {isLoading ? t("saving") : t("save")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const EditProposal = () => {
  const { t, i18n } = useTranslation();

  const schema = yup.object({
    reference: yup.string().nullable(),
    status: yup.string().required(t("statusRequired")),
    client: yup.string().required(t("clientRequired")),
    contactperson: yup.string().nullable(),
    location: yup.string().nullable(),
    date: yup
      .date()
      .nullable()
      .transform((curr, orig) => (orig === "" ? null : curr))
      .required(t("dateRequired"))
      .typeError(t("invalidDate")),

    dueDate: yup
      .date()
      .nullable()
      .transform((curr, orig) => (orig === "" ? null : curr))
      .required(t("dueDateRequired"))
      .typeError(t("invalidDueDate"))
      .test("dueDate-valid", t("dateBeforeDueDate"), function (dueDate) {
        const { date } = this.parent;

        // If dueDate is provided but date is missing, show specific error
        if (dueDate && !date) {
          return this.createError({
            message: t("dateBeforeDueDate"),
          });
        }

        return true;
      })
      .test("dueDate-after-date", t("dueDateAfterDate"), function (dueDate) {
        const { date } = this.parent;

        // Require dueDate to be strictly greater than date
        if (dueDate && date) {
          return dueDate > date;
        }

        return true;
      }),
    document: yup.string().nullable(),
    paymentTerms: yup.string().required(t("paymentTermsRequired")),
    invoiceType: yup.string().required(t("invoiceTypeRequired")),
    workType: yup.string().required(t("workTypeRequired")),
    startDate: yup
      .date()
      .nullable()
      .transform((curr, orig) => (orig === "" ? null : curr))
      .required(t("startDateRequired"))
      .typeError(t("invalidStartDate")),
    endDate: yup
      .date()
      .nullable()
      .transform((curr, orig) => (orig === "" ? null : curr))
      .typeError(t("invalidEndDate"))
      .test(
        "endDate-after-startDate",
        t("endDateAfterStartDate"),
        function (endDate) {
          const { startDate } = this.parent;
          if (endDate && startDate) {
            return endDate > startDate;
          }
          return true;
        }
      ),
    cost: yup.number()
      .nullable()
      .transform((curr, orig) => (orig === "" ? null : curr)),
    jobFrequency: yup.string().nullable(),
    jobFrequencyCount: yup.number()
      .nullable()
      .transform((curr, orig) => (orig === "" ? null : curr)),
    billingCycle: yup.string().nullable(),
  });

  const locationData = useLocation();
  const pathProposalId = locationData.pathname.split("/")[2];
  const proposalId =
    pathProposalId && pathProposalId !== ":proposalId"
      ? pathProposalId
      : localStorage.getItem("proposalId");
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [proposalData, setProposalData] = useState();
  const [data, setData] = useState([]);
  const relationsRef = useRef(data);
  relationsRef.current = data;
  const [location, setLocation] = useState("");
  const [contactperson, setContactperson] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientNumber, setClientNumber] = useState();

  const [date, setDate] = useState(null);
  const [dueDate, setDueDate] = useState(null);
  const [error, setError] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [locationId, setLocationId] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [savedProposal, setSavedProposal] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedWorkType, setSelectedWorkType] = useState(null);
  const [proposalDetailId, setProposalDetailId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [locationDetails, setLocationDetails] = useState(null);
  const [proposalDetailsData, setProposalDetailsData] = useState();
  const [proposalDetail, setProposalDetail] = useState();
  const [newWorktype, setNewWorkType] = useState();
  const [newClient, setNewClient] = useState();
  const { config, locations } = useAppContext();
  const userLocations = useMemo(
    () => [...locations].sort((a, b) => (a.Name || "").localeCompare(b.Name || "")),
    [locations]
  );
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [taskWithSubtasks, setTaskWithSubtasks] = useState([]);
  const [enrichedTasks, setEnrichedTasks] = useState([]);
  const [isTaskLoading, setIsTaskLoading] = useState(false);
  const [selectedSubtasksMap, setSelectedSubtasksMap] = useState({});
  const [newSubtaskName, setNewSubtaskName] = useState({});
  const [addingSubtaskMap, setAddingSubtaskMap] = useState({});
  const subtaskInputRefs = useRef({});
  const [selectedFormData, setSelectedFormData] = useState(null);
  const [userDetails, setUserDetails] = useState({});
  const [logoSrc, setLogoSrc] = useState(null);
  const [proposalStartDate, setProposalStartDate] = useState(null);
  const customerId = localStorage.getItem("customerId");
  const [templates, setTemplates] = useState([]);
  const [proposalDetailsTemplateData, setProposalDetailsTemplateData] = useState([]);
  const hasShownToast = useRef(false);

  const domainCfg = getDomainConfig();
  const currencySymbol = domainCfg.financialLabels.currencySymbol;
  const decimalSeparator = domainCfg.financialLabels.decimalSeparator;
  const costPlaceholder = domainCfg.financialLabels.costPlaceholder;
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [savedAttachments, setSavedAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);

  const [currentVersionId, setCurrentVersionId] = useState(null);
  const [currentVersionNo, setCurrentVersionNo] = useState(null);
  const [isVersionLocked, setIsVersionLocked] = useState(false);
  const [pendingDeleteAttachmentIds, setPendingDeleteAttachmentIds] = useState([]);
  const [pendingClientChange, setPendingClientChange] = useState(null);
  const [showClientChangeModal, setShowClientChangeModal] = useState(false);

  const [currentStep, setCurrentStep] = useState(1);
  const [activeAccordionKey, setActiveAccordionKey] = useState("1");
  const [isDateOpen, setIsDateOpen] = useState(false);
  const [isDueDateOpen, setIsDueDateOpen] = useState(false);
  const [isStartDateOpen, setIsStartDateOpen] = useState(false);
  const [isEndDateOpen, setIsEndDateOpen] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const loadTemplates = async () => {
      const data = await fetchTemplatesByCustomer(i18n.language);
      setTemplates(data);
    };
    loadTemplates();
  }, [i18n.language]);

  useEffect(() => {
    const userId = localStorage.getItem("UserId");
    if (userId) {
      fetchUserData(userId);
    }
  }, []);

  useEffect(() => {
    const getLogo = async () => {
      const src = await fetchLogoSrc(customerId);
      setLogoSrc(src);
    };
    getLogo();
  }, []);

  useEffect(() => {
    if (!currentVersionId) return;

    const fetchProposalTemplate = async () => {
      try {
        const resp = await axios.get(
          `${process.env.REACT_APP_API_URL}/proposalDetailsTemplateRoute/getProposalTemplate/version/${currentVersionId}`,
          {
            headers: {
              Authorization:
                process.env.REACT_APP_BasicAuthSecret,
            },
          }
        );

        setProposalDetailsTemplateData(resp.data || []);
      } catch (err) {
        console.error(
          "Failed to load proposal template details",
          err
        );
      }
    };

    fetchProposalTemplate();
  }, [currentVersionId]);

  const fetchUserData = async (userId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/userRoute/getUserById/${userId}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );

      setUserDetails(response.data);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const fetchSettings = async (customerId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/settingsRoute/getSettingsBranding/${customerId}`,
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        }
      );
      return response.data || {};
    } catch (error) {
      console.error("Error fetching settings:", error);
      return {};
    }
  };

  const renderTemplate = async (templateKey, formData = {}) => {
    setIsLoading(true);

    const selectedClientData = data.find(
      (item) => item.RelationNumber === formData.client
    );

    const communicationUserId = localStorage.getItem("UserId") || null;
    const customerId = localStorage.getItem("customerId");
    const settings = await fetchSettings(customerId);
    const rawCost = formData.cost;
    const parsedCost =
      rawCost != null && rawCost !== ""
        ? parseFloat(String(rawCost).replace(",", "."))
        : null;

    const activeVersionId = currentVersionId || proposalData?.CurrentVersionId || null;
    const isDetailMatchingVersion =
      proposalDetailsData?.ProposalVersionId &&
      activeVersionId &&
      proposalDetailsData.ProposalVersionId === activeVersionId;

    const activeProposalDetailId = isDetailMatchingVersion ? proposalDetailId : null;

    const updatedformData = {
      ...formData,
      communicationUserId,
      customerId,
      proposalId,
      proposalVersionId: activeVersionId,
      location,
      contactperson,
      selectedClientData,
      setShowModal,
      proposalDetailId: activeProposalDetailId,
      templateKey,
      setProposalDetailId,
      guid: generateGUID(),
      locationDetails,
      setNewWorkType,
      setNewClient,
      setClientNumber,
      setProposalDetail,
      enrichedTasks,
      taskWithSubtasks,
      selectedSubtasks: formData.selectedSubtasks,
      proposalDetailsData: isDetailMatchingVersion ? proposalDetailsData : null,
      adminName: userDetails?.Name,
      UserEmailaddress: userDetails?.Emailaddress,
      UserPhoneNumber: userDetails?.PhoneNumber,
      companySignature: userDetails?.CompanySignature ?? null,
      companySignatureDate: userDetails?.CompanySignatureDate ?? null,
      logoSrc,
      settings: settings,
      proposalDetailsTemplateData,
      finLabels: domainCfg.financialLabels,
      cost: isNaN(parsedCost) ? null : parsedCost,
      jobFrequency: formData.jobFrequency || null,
      jobFrequencyCount: formData.jobFrequencyCount
        ? parseInt(formData.jobFrequencyCount, 10)
        : null,
      billingCycle: formData.billingCycle || null,
    };

    setSelectedTemplate({
      TemplateId: formData.templateId,
      TemplateName: formData.templateName,
      WorkType: formData.workType,
    });
    setSelectedFormData(updatedformData);
    setShowModal(true);
    setIsLoading(false);
  };

  const handleCreateDocument = (temp) => {

    if (!getValues("client") || !getValues("workType")) {
      toast.error(t("fillAllRequiredFields"), {
        position: "top-right",
        autoClose: 5000,
      });
      trigger();
      return;
    }

    const selectedSubtasksArray = [];
    taskWithSubtasks.forEach((task) => {
      const selected = selectedSubtasksMap[task.ProposalTaskId] || new Set();
      (task.Subtasks || []).forEach((subtask) => {
        const subId = subtask.SubtaskId || subtask.id;
        const isChecked = selected.has(subId);

        // Custom subtasks must always be sent (checked or not) so an
        // unchecked one is persisted instead of lost, matching default behaviour.
        if (subtask?.isCustom) {
          selectedSubtasksArray.push({
            proposalTaskId: task.ProposalTaskId,
            taskId: task.TaskId,
            subtaskId: subId,
            subtaskName: subtask.name || subtask.Subtask,
            isCustom: true,
            selected: isChecked,
            selectedDays: task.recurrence?.selectedDays || [],
            monthlyDay: task.recurrence?.monthlyDay || null,
          });
          return;
        }

        // Default subtasks: unchecked ones are simply omitted.
        if (!isChecked) return;
        selectedSubtasksArray.push({
          proposalTaskId: task.ProposalTaskId,
          taskId: task.TaskId,
          subtaskId: subId,
          selectedDays: task.recurrence?.selectedDays || [],
          monthlyDay: task.recurrence?.monthlyDay || null,
        });
      });
    });
    const currentValues = getValues();
    const fullFormData = {
      ...currentValues,

      cost:
        currentValues.cost !== undefined
          ? currentValues.cost
          : watchedCost,

      jobFrequency:
        currentValues.jobFrequency !== undefined
          ? currentValues.jobFrequency
          : watchedJobFrequency,

      jobFrequencyCount:
        currentValues.jobFrequencyCount !== undefined
          ? currentValues.jobFrequencyCount
          : watchedJobFrequencyCount,

      billingCycle:
        currentValues.billingCycle !== undefined
          ? currentValues.billingCycle
          : watchedBillingCycle,

      selectedSubtasks: selectedSubtasksArray,
    };

    renderTemplate(temp, fullFormData);
  };

  const methods = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      status: "0",
      proposalNumber: "",
      date: null,
      dueDate: null,
      cost: "",
      jobFrequency: "PerWeek",
      clientSignatureRequired: true,
      pauseRequired: true,
      jobFrequencyCount: 1,
      billingCycle: "",
    },
  });

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
    setValue,
    getValues,
    trigger,
    watch
  } = methods;

  const watchedCost = watch("cost");
  const watchedJobFrequency = watch("jobFrequency");
  const watchedJobFrequencyCount = watch("jobFrequencyCount");
  const watchedBillingCycle = watch("billingCycle");
  const watchedStartDate = watch("startDate");
  const watchedFormValues = watch();

  const proposalSteps = [
    {
      id: 1,
      title: t("clientProposalInformation"),
      subtitle: t("clientProposalInfoSub"),
    },
    {
      id: 2,
      title: t("requestAndSchedule"),
      subtitle: t("requestScheduleSub"),
    },
    {
      id: 3,
      title: t("pricingAndFrequency"),
      subtitle: t("pricingFrequencySub"),
    },
    {
      id: 4,
      title: t("documentsAndComments"),
      subtitle: t("documentsCommentsSub"),
    },
  ];

  const proposalStepFields = {
    1: ["client", "contactperson", "workType", "templateId", "status", "reference"],
    2: ["date", "dueDate", "startDate", "endDate", "startTime"],
    3: [
      "paymentTerms",
      "invoiceType",
      "billingCycle",
      "cost",
      "jobFrequency",
      "jobFrequencyCount",
    ],
    4: ["proposalComment"],
  };

  const proposalRequiredByStep = {
    1: ["client", "status"],
    2: ["date", "dueDate", "startDate"],
    3: ["paymentTerms", "invoiceType"],
    4: [],
  };

  const completedSteps = useMemo(() => {
    return Object.keys(proposalRequiredByStep)
      .filter((stepId) => {
        const requiredFields = proposalRequiredByStep[stepId];
        if (requiredFields.length === 0) return true;
        return requiredFields.every((field) => {
          const val = watchedFormValues[field];
          return val !== undefined && val !== null && val !== "";
        });
      })
      .map(Number);
  }, [watchedFormValues]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const fieldsWithErrors = Object.keys(errors);
      trigger(fieldsWithErrors);
    }
  }, [i18n.language]);

  const setSelectedClient = (val) => {
    for (const relation of data) {
      if (relation.RelationNumber === val) {
        const selectedLocation = relation.City;
        const selectedContactperson = getTranslatedSalutation(relation, t);
        setLocation(selectedLocation);
        setContactperson(selectedContactperson);
        setClientName(relation.Name);
        setClientNumber(relation.RelationNumber);
        setValue("client", val);
        trigger("client");
        break;
      }
    }
    return null;
  };

  useEffect(() => {
    if (!proposalData?.TemplateId) return;
    if (templates.length === 0) return;

    const matchedTemplate = templates.find(
      (t) => String(t.TemplateId) === String(proposalData.TemplateId)
    );

    if (matchedTemplate) {
      // Template found for current language → prefill
      setValue("templateId", matchedTemplate.TemplateId);
      setValue("workType", matchedTemplate.WorkType);
      setValue("templateName", matchedTemplate.TemplateName);

      setSelectedTemplate({
        TemplateId: matchedTemplate.TemplateId,
        TemplateName: matchedTemplate.TemplateName,
        WorkType: matchedTemplate.WorkType,
      });
      setSelectedWorkType(matchedTemplate.WorkType);
      hasShownToast.current = false;
    }
    else if (!hasShownToast.current) {
      hasShownToast.current = true;

      const customerId = localStorage.getItem("customerId");
      if (!customerId) return;

      const fetchAllTemplates = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_API_URL}/proposalTemplateSectionsRoute/getProposalTemplates/${customerId}`,
            {
              headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
            }
          );

          const allTemplates = res.data || [];
          const originalTemplate = allTemplates.find(
            (t) => String(t.TemplateId) === String(proposalData.TemplateId)
          );

          const originalLang = originalTemplate?.LanguageCode || "nl";

          toast.error(
            t("pleaseSelectLanguageToViewWorkType", {
              lang: originalLang.toUpperCase(),
            }),
            {
              position: "top-right",
              autoClose: 5000,
            }
          );
        } catch (err) {
          console.error("Error fetching all templates:", err);
          toast.error(
            t("pleaseSelectLanguageToViewWorkType", { lang: "NL" }),
            { position: "top-right", autoClose: 5000 }
          );
        }
      };

      fetchAllTemplates();
      // Reset invalid fields
      setValue("workType", "");
      setValue("templateId", "");
      trigger("workType");
      setSelectedTemplate({});
      setSelectedWorkType("");
    }
  }, [proposalData, templates, i18n.language, setValue, trigger, t])

  const fetchProposal = async (proposalId) => {
    if (!locationId) return;

    try {
      setLoading(true);

      const customerId = localStorage.getItem("customerId");
      if (!customerId) {
        setError("CustomerId is missing.");
        setLoading(false);
        return;
      }

      const [relationResponse, response] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_API_URL}/relationRoute/relations`,
          {
            params: { locationId, customerId, fields: "summary" },
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret
            }
          }
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL}/proposalRoute/getProposalById/${proposalId}`, {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
        ),
      ]);
      const sortedRelations = [...relationResponse.data].sort((a, b) =>
        (a.Name || "").localeCompare(b.Name || "")
      );
      setData(sortedRelations);
      const location = config.location.LocationId;

      if (response.data.LocationId != location) {
        navigate("/proposals");
      }

      const proposalData = response.data;
      setProposalData(proposalData);
      setCurrentVersionId(proposalData.CurrentVersionId || null);
      setCurrentVersionNo(proposalData.CurrentVersionNo || null);
      setIsVersionLocked(proposalData.Status === "1");

      // Convert ISO strings to moment objects for date pickers
      const proposalDate = proposalData.Date
        ? moment(proposalData.Date).toDate()
        : null;
      const proposalDueDate = proposalData.DueDate
        ? moment(proposalData.DueDate).toDate()
        : null;
      const proposalStartDate = proposalData.StartDate
        ? moment(proposalData.StartDate).toDate()
        : null;
      setProposalStartDate(proposalStartDate);

      const proposalEndDate = proposalData.EndDate
        ? moment(proposalData.EndDate).toDate()
        : null;

      // Set form fields

      setValue("reference", proposalData.Reference);
      setValue("status", proposalData.Status);
      setValue("client", proposalData.Client);
      setValue("locationId", proposalData.LocationId);
      setSelectedLocationId(proposalData.LocationId);
      setDate(proposalDate);
      setDueDate(proposalDueDate);
      setValue("date", proposalDate);
      setValue("dueDate", proposalDueDate);
      setValue("startDate", proposalStartDate);
      setValue("endDate", proposalEndDate);
      setValue("cost", proposalData.ProposalCost ?? "");
      setValue("jobFrequency", proposalData.JobFrequency ?? null);
      setValue("jobFrequencyCount", proposalData.JobFrequencyCount ?? null);
      setValue("billingCycle", proposalData.BillingCycle ?? "");

      // For time, convert UTC datetime string from DB to a Date object for DatePicker
      if (proposalData.StartTime) {
        setValue("startTime", parseTimeStringToDate(proposalData.StartTime));
      } else {
        setValue("startTime", null); // Set to null if no time is available
      }
      setValue("paymentTerms", proposalData.PaymentTerms);
      setValue("invoiceType", proposalData.InvoiceType);
      setLocation(proposalData.Location);
      setValue("workType", proposalData.WorkType);

      setSelectedWorkType(proposalData.WorkType);
      setValue("proposalComment", proposalData.ProposalComment || "");
      // setValue("clientSignatureRequired", Boolean(proposalData.ClientSignatureRequired));
      // setValue("pauseRequired", Boolean(proposalData.PauseRequired));

      setNewWorkType(proposalData.WorkType);
      setNewClient(proposalData.Client);
      setClientNumber(proposalData.Client);
      // Automatically trigger setSelectedClient to update "Contact persoon" and "Locatie"
      setSelectedClient(proposalData.Client); // Pass the client value
      const selectedClient = relationResponse.data.find(
        (client) => client.RelationNumber === proposalData.Client
      );
      if (selectedClient) {
        setClientName(selectedClient.Name); // Set the client name from fetched data
        setContactperson(getTranslatedSalutation(selectedClient, t));
      }
      // setProposalDetailId(proposalData.ProposalDetailId);
      fetchProposalDetails(
        proposalData.ProposalId,
        proposalData.CurrentVersionId
      );
    } catch (error) {
      console.error("Error fetching offerte data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (proposalData?.StartDate) {
      setValue("startDate", proposalData.StartDate);
    }
  }, [proposalData, setValue]);

  const startDate = useWatch({ control, name: "startDate" });

  const fetchAndShowProposalTasks = async (proposalId, versionId) => {
    try {
      setIsTaskLoading(true);
      const params = versionId ? { proposalVersionId: versionId } : {};
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/proposalRoute/${proposalId}/tasks`,
        {
          params,
          headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
        }
      );
      const tasks = res.data || [];
      await fetchSubtasksForTasks(tasks);
    } catch (error) {
      console.error("Error fetching proposal tasks:", error);
    } finally {
      setIsTaskLoading(false); // Hide loader
    }
  };

  const fetchLocationdata = async (locationId) => {
    try {
      const locationResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/locationRoute/getById/${locationId}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      setLocationDetails(locationResponse.data);
    } catch (error) {
      console.error("Error fetching relation data:", error);
    }
  };

  const fetchProposalDetails = async (
    proposalId,
    proposalVersionId
  ) => {
    const fetchDetails = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/proposalDetailsRoute/getProposalDetailsByVersionId/${proposalVersionId}`,
          {
            headers: {
              Authorization:
                process.env.REACT_APP_BasicAuthSecret
            }
          }
        );

        if (response.data && response.data.ProposalDetailId) {
          setProposalDetailsData(response.data);
          setProposalDetailId(response.data.ProposalDetailId);
        } else {
          setProposalDetailsData(null);
          setProposalDetailId(null);
        }
      } catch (error) {
        console.error("Error fetching proposal details:", error);
        setProposalDetailsData(null);
        setProposalDetailId(null);
      }
    };

    await Promise.all([fetchDetails(), fetchSavedAttachments(proposalId)]);
  };

  useEffect(() => {
    fetchRoleAndClaims({
      navigate,
      claimType: "proposal",
      claimValue: "edit",
      setHasAccess,
      setIsLoading,
    });
  }, [navigate]);

  useEffect(() => {
    const location = localStorage.getItem("LocationId");
    fetchLocationdata(location);
    setLocationId(location);
  }, []);

  useEffect(() => {
    fetchProposal(proposalId);
  }, [locationId, config.location]);

  const handleDiscard = () => {
    reset();

    navigate(`${paths.proposalDetails.replace(":proposalId", proposalId)}`);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const handleShow = () => {
    setShowModal(true);
  };

  const parseTimeStringToDate = (isoString) => {
    if (!isoString) return null;
    const utcMoment = moment.utc(isoString);
    return utcMoment.local().toDate();
  };

  const fetchSavedAttachments = async (proposalId) => {
    try {
      setAttachmentsLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/proposalAttachmentRoute/getByProposal/${proposalId}`,
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );
      setSavedAttachments(res.data || []);
    } catch (err) {
      console.error("Error fetching saved attachments:", err);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const handleFilesSelected = (files) => {
    const fileArray = Array.from(files);
    const validFiles = [];

    fileArray.forEach((file) => {
      if (file.size > 2 * 1024 * 1024) {
        toast.error(t("fileSizeExceeded"), {
          position: "top-right",
          autoClose: 4000,
        });
        return;
      }
      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...validFiles]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesSelected(e.dataTransfer.files);
    }
  };

  const uploadAttachmentsForProposal = async (proposalId, relationId, files, proposalVersionId = null) => {
    for (const file of files) {
      try {
        const ext = file.name.split(".").pop();
        const formData = new FormData();
        formData.append("file", file);
        formData.append("proposalId", proposalId);
        formData.append("proposalVersionId", proposalVersionId);  // use param, not currentVersionId from closure

        const blobRes = await axios.post(
          `${process.env.REACT_APP_API_URL}/blobRoute/uploadAttachment/${relationId}`,
          formData,
          {
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const { blobUrl, blobName } = blobRes.data;

        await axios.post(
          `${process.env.REACT_APP_API_URL}/proposalAttachmentRoute/create`,
          {
            proposalId,
            proposalVersionId,  // same value — now guaranteed to match the blob path
            relationId,
            blobUrl,
            blobName,
            originalName: file.name,
            fileType: ext,
          },
          {
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
          }
        );
      } catch (err) {
        console.error(`Failed to upload attachment: ${file.name}`, err);
        const reason = err.response?.data?.code ? err.response.data.message : null;
        toast.error(`${t("attachmentUploadFailed")}: ${file.name}${reason ? ` (${reason})` : ""}`, {
          position: "top-right",
          autoClose: reason ? 6000 : undefined,
        });
      }
    }
  };

  const handleDeleteSavedAttachment = (attachmentId) => {
    setPendingDeleteAttachmentIds((prev) => [...prev, attachmentId]);
    setSavedAttachments((prev) =>
      prev.filter((a) => a.AttachmentId !== attachmentId)
    );
  };

  const handleViewAttachment = async (attachment) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/blobRoute/getAttachment`,
        {
          params: { blobUrl: attachment.BlobUrl },  // use BlobUrl directly — no path reconstruction
          responseType: "arraybuffer",              // use arraybuffer to handle any file type
          headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
        }
      );

      const mimeType = response.headers["content-type"] || "application/octet-stream";
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (error) {
      console.error("[handleViewAttachment] ERROR:", error);
      toast.error(t("attachmentViewError"), { position: "top-right" });
    }
  };

  const onSubmit = async (data) => {

    if (!getValues("workType") || !selectedTemplate?.TemplateId) {
      toast.error(
        t("pleaseSelectLanguageToViewWorkType", {
          lang: i18n.language.toUpperCase(),
        }),
        {
          position: "top-right",
          autoClose: 4000,
        }
      );
      trigger("workType");
      return;
    }
    setIsLoading(true);

    const proposalId = proposalData.ProposalId;
    let workingVersionId = currentVersionId;

    const wasApproved = proposalData.Status === "1";

    if (proposalData.Status === "1") {
      try {
        const versionResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/proposalVersionRoute/versions/create`,
          { proposalId },
          { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        );
        workingVersionId = versionResponse.data.version.ProposalVersionId;
        const wasNewVersionCreated = versionResponse.data.created === true;

        setCurrentVersionNo(versionResponse.data.version.VersionNo);
        setCurrentVersionId(workingVersionId);

        if (wasNewVersionCreated) {
          setProposalDetailsData(null);
          setProposalDetailId(null);
          setProposalDetailsTemplateData([]);

          await fetchProposalDetails(
            proposalId,
            workingVersionId
          );

          setIsVersionLocked(false);

          setProposalData(prev => ({
            ...prev,
            Status: "0",
            IsImported: false,
            CurrentVersionId: workingVersionId,
            CurrentVersionNo:
              versionResponse.data.version.VersionNo,
            TemplateId: data.templateId,
            TemplateName: selectedTemplate.TemplateName,
            WorkType: selectedTemplate.WorkType,
          }));
        }

        // Always reset status to draft since we're editing
        proposalData.Status = "0";
        setValue("status", "0");

      } catch (err) {
        console.error("Failed to create new proposal version", err);
        toast.error(t("failedToCreateProposalVersion"));
        setIsLoading(false);
        return;
      }
    }

    const formattedStartDate = data.startDate
      ? moment(data.startDate).format("YYYY-MM-DD")
      : null;
    const formattedEndDate = data.endDate
      ? moment(data.endDate).format("YYYY-MM-DD")
      : null;
    const formattedDate = data.date
      ? moment(data.date).format("YYYY-MM-DD")
      : null;
    const formattedDueDate = data.dueDate
      ? moment(data.dueDate).format("YYYY-MM-DD")
      : null;

    let formattedStartTime = null;

    if (data.startTime instanceof Date) {
      const utcEquivalentMoment = moment(data.startTime).utc();
      formattedStartTime = `${moment().format(
        "YYYY-MM-DD"
      )}T${utcEquivalentMoment.format("HH:mm:ss.SSS")}Z`;
    } else if (typeof data.startTime === "string" && data.startTime !== "") {
      if (
        data.startTime.startsWith("1970-01-01T") &&
        data.startTime.endsWith("Z")
      ) {
        formattedStartTime = `${moment().format("YYYY-MM-DD")}T${data.startTime.split("T")[1]
          }`;
      } else {
        const [hours, minutes] = data.startTime.split(":");
        const utcEquivalentMoment = moment()
          .set({
            hour: parseInt(hours, 10),
            minute: parseInt(minutes, 10),
            second: 0,
            millisecond: 0,
          })
          .utc();
        formattedStartTime = `${moment().format(
          "YYYY-MM-DD"
        )}T${utcEquivalentMoment.format("HH:mm:ss.SSS")}Z`;
      }
    }
    // Step 1: Delete existing proposal detail if work type has changed

    if (
      selectedTemplate?.WorkType !== proposalData?.WorkType &&
      !wasApproved
    ) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/proposalDetailsRoute/handleProposalDetailsByVersionId/${workingVersionId}`, {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
        );
        setProposalDetailId(null);
      } catch (error) {
        // Silently ignore any error (e.g., not found, already deleted)
      }
    }

    const updatedFormData = {
      ...data,
      employee: proposalData.Employee,
      proposalId,
      proposalVersionId: workingVersionId,
      communicationUserId: localStorage.getItem("UserId") || null, 
      contactperson,
      location,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      date: formattedDate,
      dueDate: formattedDueDate,
      startTime: formattedStartTime,
      workType: selectedTemplate.WorkType,
      templateId: data.templateId,
      templateName: selectedTemplate.TemplateName,
      proposalComment: data.proposalComment,
      cost: data.cost ?? null,
      jobFrequency: data.jobFrequency ?? null,
      jobFrequencyCount: data.jobFrequencyCount ?? null,
      billingCycle: data.billingCycle ?? null,
      clientSignatureRequired: data.clientSignatureRequired ?? false,
      pauseRequired: data.pauseRequired ?? false,
      status: wasApproved ? "0" : data.status,
      curruntVersionId: currentVersionId,
      curruntVersionNo: currentVersionNo,
      isImported: false,
      wasApproved,
    };

    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/proposalRoute/updateProposal/${proposalId}`,
        updatedFormData,
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );

      if (response.status === 200) {
        const savedProposalData = response.data.proposal;

        if (savedProposalData?.CurrentVersionId && savedProposalData.CurrentVersionId !== currentVersionId) {
          setCurrentVersionId(savedProposalData.CurrentVersionId);
          setCurrentVersionNo(savedProposalData.CurrentVersionNo);
        }

        setSavedProposal(savedProposalData);
        setProposalData(savedProposalData);

        if (uploadedFiles.length > 0) {
          const currentRelation = relationsRef.current.find(
            (r) => r.RelationNumber === getValues("client")
          );
          if (currentRelation?.RelationId) {
            await uploadAttachmentsForProposal(
              proposalId,
              currentRelation.RelationId,
              uploadedFiles,
              workingVersionId
            );
            setUploadedFiles([]);
            await fetchSavedAttachments(
              proposalId,
              workingVersionId
            );
          }
        }

        if (pendingDeleteAttachmentIds.length > 0) {
          for (const attachmentId of pendingDeleteAttachmentIds) {
            try {
              await axios.delete(
                `${process.env.REACT_APP_API_URL}/proposalAttachmentRoute/delete/${attachmentId}`,
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
              );
            } catch (err) {
              console.error("Error deleting staged attachment:", err);
              // non-fatal — don't block navigation
            }
          }
          setPendingDeleteAttachmentIds([]);
        }
        setShowTaskForm(true);
        fetchAndShowProposalTasks(
          proposalId || proposalData?.ProposalId,
          workingVersionId
        );
      }
    } catch (error) {
      console.error("There was an error submitting the form:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const pendingClientName = pendingClientChange
  ? data.find((r) => r.RelationNumber === pendingClientChange.selectedValue)?.Name
  : null;

  const loadingOrRedirect = handleLoadingAndAccess(isLoading, hasAccess);

  if (loadingOrRedirect) {
    return loadingOrRedirect; // Render the loading spinner or handle navigation
  }

  const RecurrenceColumns = ({ recurrence, taskLabel, rowIdx }) => {
    if (!recurrence) return null;

    const renderToggle = (dayKey, isChecked) => (
      <td key={dayKey} className="text-center recurrence-column">
        {isChecked ? (
          <div className="text-success">
            <i className="fa fa-check" aria-hidden="true"></i>
          </div>
        ) : null}
      </td>
    );

    const freq = recurrence.frequency?.toLowerCase();

    if (freq === "daily" || freq === "weekly") {
      const days = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];
      return days.map((day) =>
        renderToggle(day, freq === "daily" ? true : recurrence.selectedDays?.includes(day))
      );
    }

    // For Monthly
    if (freq === "monthly") {
      const repeatEvery = recurrence.repeatEvery || 1;
      return Array.from({ length: 12 }, (_, i) =>
        renderToggle(`month-${i}`, i % repeatEvery === 0)
      );
    }

    if (freq === "yearly") {
      const adv = recurrence.advancedRecurrence || {};
      const activeMonths = new Set();

      if (Array.isArray(adv.dates) && adv.dates.length > 0) {
        adv.dates.forEach((entry) => {
          if (entry && entry.month !== undefined && entry.month !== null && entry.month !== "") {
            activeMonths.add(Number(entry.month));
          }
        });
      } else if (adv.yearlyType === "weekday_occurrence" && Array.isArray(adv.yearlyOccurrences)) {
        adv.yearlyOccurrences.forEach((occ) => {
          if (occ && occ.month !== undefined && occ.month !== null && occ.month !== "") {
            activeMonths.add(Number(occ.month));
          }
        });
      } else {
        const months = adv.yearlyMonth;
        if (Array.isArray(months)) {
          months.forEach((m) => activeMonths.add(Number(m)));
        } else if (months !== undefined && months !== null && months !== "") {
          activeMonths.add(Number(months));
        } else if (recurrence.yearlyMonth !== undefined && recurrence.yearlyMonth !== null) {
          activeMonths.add(Number(recurrence.yearlyMonth));
        }
      }

      return Array.from({ length: 12 }, (_, i) =>
        renderToggle(`month-${i}`, activeMonths.has(i))
      );
    }

    return null;
  };

  const fetchSubtasksForTasks = async (tasks) => {
    const updatedSubtaskMap = {};

    const results = await Promise.all(
      tasks.map(async (task) => {
        try {
          const [subtasksRes, taskDetailsRes] = await Promise.all([
            axios.get(
              `${process.env.REACT_APP_API_URL}/proposalRoute/proposalTasks/${task.ProposalTaskId}/subtasks`, {
              headers: {
                Authorization: process.env.REACT_APP_BasicAuthSecret
              }
            }
            ),
            axios.get(
              `${process.env.REACT_APP_API_URL}/taskRoute/getTaskById/${task.TaskId}`, {
              headers: {
                Authorization: process.env.REACT_APP_BasicAuthSecret
              }
            }
            ),
          ]);

          const allSubtasks = subtasksRes.data || [];

          updatedSubtaskMap[task.ProposalTaskId] = new Set(
            allSubtasks.filter((s) => s.selected !== false).map((s) => s.id || s.SubtaskId)
          );

          return {
            ...task,
            label: taskDetailsRes.data?.TaskName || "Unnamed Task",
            Subtasks: allSubtasks.map((s) => ({ ...s, SubtaskId: s.id || s.SubtaskId, Subtask: s.name || s.Subtask })),
            recurrence: {
              frequency: task.Frequency,
              repeatEvery: task.RepeatEvery,
              selectedDays: (() => {
                try {
                  const raw = task.SelectedDays;
                  if (Array.isArray(raw)) return raw;
                  if (typeof raw === "string") {
                    const parsed = JSON.parse(raw);
                    if (Array.isArray(parsed)) return parsed;
                    if (typeof parsed === "object" && parsed !== null) {
                      return parsed.selectedDays || [];
                    }
                  }
                  return [];
                } catch {
                  return [];
                }
              })(),
              monthlyDay: task.MonthlyDay,
              advancedRecurrence: (() => {
                try {
                  const raw = task.SelectedDays;
                  if (typeof raw === "string") {
                    const parsed = JSON.parse(raw);
                    if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
                      return parsed;
                    }
                  }
                } catch {}
                return null;
              })()
            },
          };
        } catch (error) {
          console.error("Error enriching task:", error);
          return null;
        }
      })
    );

    setTaskWithSubtasks(results.filter(Boolean));
    setSelectedSubtasksMap(updatedSubtaskMap);
  };

  const handleSaveSelectedTasks = (tasks) => {
    setSelectedTasks(tasks);
    fetchSubtasksForTasks(tasks);
  };

  const handleAddCustomSubtask = (proposalTaskId) => {
    const name = String(newSubtaskName[proposalTaskId] || "").trim();
    if (!name) return;
    const customId = `custom-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const custom = {
      id: customId,
      SubtaskId: customId,
      name,
      Subtask: name,
      isCustom: true,
      selected: true,
      selectedDays: [],
    };
    setTaskWithSubtasks((tasks) => tasks.map((task) => task.ProposalTaskId === proposalTaskId ? { ...task, Subtasks: [...(task.Subtasks || []), custom] } : task));
    setSelectedSubtasksMap((previous) => ({ ...previous, [proposalTaskId]: new Set([...(previous[proposalTaskId] || []), custom.id]) }));
    setNewSubtaskName((previous) => ({ ...previous, [proposalTaskId]: "" }));
  };

  const openAddSubtask = (proposalTaskId) => {
    setAddingSubtaskMap((prev) => ({ ...prev, [proposalTaskId]: true }));
    setTimeout(() => subtaskInputRefs.current[proposalTaskId]?.focus(), 0);
  };

  const closeAddSubtask = (proposalTaskId) => {
    setAddingSubtaskMap((prev) => ({ ...prev, [proposalTaskId]: false }));
    setNewSubtaskName((prev) => ({ ...prev, [proposalTaskId]: "" }));
  };

  const handleAddCustomSubtaskAndContinue = (proposalTaskId) => {
    const name = String(newSubtaskName[proposalTaskId] || "").trim();
    if (!name) return;
    handleAddCustomSubtask(proposalTaskId);
    setTimeout(() => subtaskInputRefs.current[proposalTaskId]?.focus(), 0);
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/proposalRoute/tasks/delete/${taskId}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );

      const updatedTasks = taskWithSubtasks.filter(
        (task) => task.ProposalTaskId !== taskId
      );
      setTaskWithSubtasks(updatedTasks);
      toast.success(t("taskDeleted"), {
        position: "top-right",
      });
    } catch (error) {
      console.error("Error deleting proposal task:", error);
      toast.error(t("taskDeleteError"), {
        position: "top-right",
      });
    }
  };

  const handleSubtaskCheckChange = (proposalTaskId, subtaskId, isChecked) => {
    setSelectedSubtasksMap((prevMap) => {
      const taskSubtasks = prevMap[proposalTaskId] || new Set();
      if (isChecked) {
        taskSubtasks.add(subtaskId);
      } else {
        taskSubtasks.delete(subtaskId);
      }
      return {
        ...prevMap,
        [proposalTaskId]: new Set(taskSubtasks),
      };
    });
  };

  const handleProposalNext = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const valid = await trigger(proposalStepFields[currentStep]);
    if (!valid) {
      toast.error(t("pleaseFillRequiredFields"), {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    const nextStep = Math.min(currentStep + 1, 4);
    setCurrentStep(nextStep);
    setActiveAccordionKey(String(nextStep));
  };

  const handleProposalBack = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const prevStep = Math.max(currentStep - 1, 1);
    setCurrentStep(prevStep);
    setActiveAccordionKey(String(prevStep));
  };

  const goToProposalStep = async (stepId) => {
    if (stepId === currentStep) return;
    if (stepId > currentStep) {
      const valid = await trigger(proposalStepFields[currentStep]);
      if (!valid) {
        toast.error(t("pleaseFillRequiredFields"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
    }
    setCurrentStep(stepId);
    setActiveAccordionKey(String(stepId));
  };

  const onProposalInvalid = (formErrors) => {
    const errorFields = Object.keys(formErrors);
    if (errorFields.length === 0) return;

    const stepWithError = Object.keys(proposalStepFields).find((stepId) =>
      proposalStepFields[stepId].some((field) => errorFields.includes(field))
    );

    if (stepWithError) {
      setCurrentStep(Number(stepWithError));
      setActiveAccordionKey(stepWithError);
    }
    toast.error(t("pleaseFillRequiredFields"), {
      position: "top-right",
      autoClose: 3000,
    });
  };

  const handleProposalSave = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    handleSubmit(onSubmit, onProposalInvalid)();
  };

  return (
    <FormProvider {...methods}>
      <Row className="g-3 mb-3">
        <Col lg={12} xxl={12}>
          <Card className="mb-3 h-100">
            <Background image={corner4} className="bg-card" />
            <Card.Body className="position-relative pe-3 pb-2">
              <Row className="flex-between-center">
                <Col md>
                  <h5 className="mb-2 mb-md-0">{t("editOffer")}</h5>
                  <Breadcrumb>
                    <Breadcrumb.Item
                      as={Link}
                      to="/proposals"
                      className="fw-semi-bold mb-1 fs-9 data-font-colour"
                      active
                    >
                      {t("proposals")}
                    </Breadcrumb.Item>
                    {proposalData && (
                      <Breadcrumb.Item
                        onClick={() =>
                          navigate(
                            `${paths.proposalDetails.replace(
                              ":proposalId",
                              proposalId
                            )}`
                          )
                        }
                        className="fw-semi-bold mb-1 fs-9 cursor-pointer data-font-colour"
                        active
                      >
                        {proposalData.ProposalNumber}
                      </Breadcrumb.Item>
                    )}

                    <Breadcrumb.Item
                      className="fw-bold mb-1 fs-9 data-font-colour"
                      active
                    >
                      {t("edit")}
                    </Breadcrumb.Item>
                  </Breadcrumb>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {!location && !contactperson && !proposalData ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t("loading")}</span>
          </Spinner>
        </div>
      ) : (
        <>
          {!showTaskForm ? (
            <>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className="g-3 align-items-stretch">
                  {/* ── LEFT SIDEBAR ── */}
                  <Col
                    xs="12"
                    lg="3"
                    xl="2"
                    className="rf-left-panel step-sidebar-col"
                  >
                    <Card className="mb-3 h-100 step-row-container">
                      <Card.Body>
                        {proposalSteps.map((step, index) => (
                          <div
                            key={step.id}
                            role="button"
                            tabIndex={0}
                            className={`d-flex gap-2 pointer p-2 rounded step-row ${
                              currentStep === step.id ? "step-row-active" : ""
                            }`}
                            onClick={() => goToProposalStep(step.id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                goToProposalStep(step.id);
                              }
                            }}
                          >
                            <div className="d-flex flex-column align-items-center step-circle-col">
                              <span
                                className={`step-circle ${
                                  currentStep === step.id ? "active" : ""
                                } ${
                                  completedSteps.includes(step.id)
                                    ? "completed"
                                    : ""
                                }`}
                              >
                                {step.id}
                              </span>
                              {index < proposalSteps.length - 1 && (
                                <div
                                  className={`step-connector-line ${
                                    completedSteps.includes(step.id)
                                      ? "completed"
                                      : ""
                                  } ${
                                    currentStep === step.id ? "active" : ""
                                  }`}
                                />
                              )}
                            </div>
                            <div className="step-text-col">
                              <h5
                                className={`fw-medium ${
                                  currentStep === step.id
                                    ? "text-primary"
                                    : ""
                                }`}
                              >
                                {step.title}
                              </h5>
                              <small className="mt-2 text-secondary">
                                {step.subtitle}
                              </small>
                            </div>
                          </div>
                        ))}
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* ── RIGHT PANEL ── */}
                  <Col
                    xs="12"
                    lg="9"
                    xl="10"
                    className="rf-right-panel rf-form-col"
                  >
                    <Accordion
                      activeKey={activeAccordionKey}
                      onSelect={(key) => {
                        if (key) goToProposalStep(Number(key));
                      }}
                      className="rf-accordion"
                    >
                      {/* ── STEP 1: Client & Proposal Information ── */}
                      <Accordion.Item eventKey="1" className="rf-accordion-item">
                        <Accordion.Header>
                          <span className="h5 fw-medium mb-0 d-flex align-items-center">
                            <span className="rf-header-icon bg-client">
                              <FontAwesomeIcon icon={faUser} />
                            </span>
                            <span className="rf-header-text">
                              <span>{t("clientProposalInformation")}</span>
                              <span className="rf-header-subtitle">
                                {t("clientProposalInfoSub")}
                              </span>
                            </span>
                          </span>
                        </Accordion.Header>
                        <Accordion.Body>
                          <Row className="mb-3">
                            <Col md="4">
                              <Form.Group>
                                <Form.Label className="data-font-size data-font-colour mb-2">
                                  {t("client")}
                                  <span className="text-danger">*</span>
                                </Form.Label>
                                <Controller
                                  name="client"
                                  control={control}
                                  rules={{ required: t("clientRequired") }}
                                  render={({
                                    field,
                                    fieldState: { error },
                                  }) => {
                                    const options = data.map((client) => ({
                                      value: client.RelationNumber,
                                      label: client.Name,
                                    }));
                                    return (
                                      <>
                                        <Select
                                          key={i18n.language}
                                          options={options}
                                          isClearable
                                          value={
                                            options.find(
                                              (opt) => opt.value === field.value
                                            ) || null
                                          }
                                          onChange={(selected) => {
                                            const selectedValue = selected
                                              ? selected.value
                                              : "";

                                            if (
                                              proposalData?.Status === "1" &&
                                              selectedValue !==
                                                proposalData?.Client
                                            ) {
                                              setPendingClientChange({
                                                field,
                                                selectedValue,
                                              });
                                              setShowClientChangeModal(true);
                                            } else {
                                              field.onChange(selectedValue);
                                              setSelectedClient(selectedValue);
                                              setValue("client", selectedValue, {
                                                shouldValidate: true,
                                              });
                                            }
                                          }}
                                          onBlur={field.onBlur}
                                          className={`custom-select-container ${
                                            error ? "is-invalid" : ""
                                          }`}
                                          classNamePrefix="custom-select"
                                          placeholder={t("select")}
                                          noOptionsMessage={() =>
                                            t("noOptionsAvailable")
                                          }
                                        />
                                        {error && (
                                          <div className="invalid-feedback d-block">
                                            {error.message}
                                          </div>
                                        )}
                                      </>
                                    );
                                  }}
                                />
                              </Form.Group>
                            </Col>

                            <Col md="4">
                              <Form.Group>
                                <Form.Label className="data-font-size data-font-colour mb-2">
                                  {t("contactPerson")}
                                  <span className="text-danger">*</span>
                                </Form.Label>
                                <Controller
                                  name="contactperson"
                                  control={control}
                                  defaultValue={contactperson || ""}
                                  rules={{
                                    required: t("contactpersonRequired"),
                                  }}
                                  render={({
                                    field,
                                    fieldState: { error },
                                  }) => {
                                    const selectedOption = contactperson
                                      ? {
                                          value: contactperson,
                                          label: contactperson,
                                        }
                                      : null;
                                    return (
                                      <>
                                        <Select
                                          key={i18n.language}
                                          value={selectedOption}
                                          isDisabled
                                          isClearable={false}
                                          className={`custom-select-container ${
                                            error ? "is-invalid" : ""
                                          }`}
                                          classNamePrefix="custom-select"
                                          placeholder={t("select")}
                                          noOptionsMessage={() =>
                                            t("noOptionsAvailable")
                                          }
                                        />
                                        <input
                                          type="hidden"
                                          {...field}
                                          value={contactperson || ""}
                                        />
                                        {error && (
                                          <div className="invalid-feedback d-block">
                                            {error.message}
                                          </div>
                                        )}
                                      </>
                                    );
                                  }}
                                />
                              </Form.Group>
                            </Col>

                            <Col md="4">
                              <Form.Group>
                                <Form.Label className="data-font-size data-font-colour mb-2">
                                  {t("branch")}
                                </Form.Label>
                                <Controller
                                  name="location"
                                  control={control}
                                  defaultValue={location || ""}
                                  rules={{ required: t("branchRequired") }}
                                  render={({
                                    field,
                                    fieldState: { error },
                                  }) => {
                                    const selectedOption = location
                                      ? { value: location, label: location }
                                      : null;
                                    return (
                                      <>
                                        <Select
                                          key={i18n.language}
                                          value={selectedOption}
                                          isDisabled
                                          isClearable={false}
                                          className={`custom-select-container ${
                                            error ? "is-invalid" : ""
                                          }`}
                                          classNamePrefix="custom-select"
                                          placeholder={t("select")}
                                          noOptionsMessage={() =>
                                            t("noOptionsAvailable")
                                          }
                                        />
                                        <input
                                          type="hidden"
                                          {...field}
                                          value={location || ""}
                                        />
                                        {error && (
                                          <div className="invalid-feedback d-block">
                                            {error.message}
                                          </div>
                                        )}
                                      </>
                                    );
                                  }}
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col md="4">
                              <Form.Group>
                                <Form.Label className="data-font-size data-font-colour mb-2">
                                  {t("workType")}
                                  <span className="text-danger">*</span>
                                </Form.Label>
                                <Controller
                                  name="templateId"
                                  control={control}
                                  rules={{ required: t("workTypeRequired") }}
                                  render={({
                                    field,
                                    fieldState: { error },
                                  }) => {
                                    const workTypeOptions = templates.map(
                                      (template) => ({
                                        value: template.TemplateId,
                                        label: template.TemplateName,
                                        workType: template.WorkType,
                                      })
                                    );
                                    return (
                                      <>
                                        <input
                                          type="hidden"
                                          {...register("workType")}
                                        />
                                        <Select
                                          key={i18n.language}
                                          options={workTypeOptions}
                                          isClearable
                                          value={
                                            workTypeOptions.find(
                                              (opt) => opt.value === field.value
                                            ) || null
                                          }
                                          onChange={(selected) => {
                                            const selectedValue = selected
                                              ? selected.value
                                              : "";
                                            const selectedWorkType = selected
                                              ? selected.workType
                                              : "";
                                            const selectedTemplateName = selected
                                              ? selected.label
                                              : "";
                                            field.onChange(selectedValue);
                                            setValue("templateId", selectedValue);
                                            setValue("workType", selectedWorkType);
                                            setSelectedWorkType(
                                              selectedWorkType
                                            );
                                            setValue(
                                              "templateName",
                                              selectedTemplateName
                                            );
                                            setSelectedTemplate({
                                              TemplateId: selectedValue,
                                              TemplateName: selectedTemplateName,
                                              WorkType: selectedWorkType,
                                            });
                                          }}
                                          onBlur={field.onBlur}
                                          className={`custom-select-container ${
                                            error ? "is-invalid" : ""
                                          }`}
                                          classNamePrefix="custom-select"
                                          placeholder={t("select")}
                                          noOptionsMessage={() =>
                                            t("noOptionsAvailable")
                                          }
                                        />
                                        {error && (
                                          <div className="invalid-feedback d-block">
                                            {error.message}
                                          </div>
                                        )}
                                      </>
                                    );
                                  }}
                                />
                              </Form.Group>
                            </Col>

                            <Col md="4">
                              <Form.Group>
                                <Form.Label className="data-font-size data-font-colour mb-2">
                                  {t("status")}
                                  <span className="text-danger">*</span>
                                </Form.Label>
                                <Controller
                                  name="status"
                                  control={control}
                                  rules={{ required: t("statusRequired") }}
                                  render={({
                                    field,
                                    fieldState: { error },
                                  }) => {
                                    const statusOptions = [
                                      { value: "1", label: t("agreed") },
                                      { value: "2", label: t("notAgreed") },
                                      { value: "0", label: t("open") },
                                    ];
                                    return (
                                      <>
                                        <Select
                                          {...field}
                                          options={statusOptions}
                                          isDisabled={
                                            proposalData?.Status === "1"
                                          }
                                          isClearable
                                          value={
                                            statusOptions.find(
                                              (opt) => opt.value === field.value
                                            ) || null
                                          }
                                          onChange={(selected) => {
                                            const selectedValue = selected
                                              ? selected.value
                                              : "";
                                            field.onChange(selectedValue);
                                            setValue("status", selectedValue);
                                          }}
                                          onBlur={field.onBlur}
                                          className={`custom-select-container ${
                                            error ? "is-invalid" : ""
                                          }`}
                                          classNamePrefix="custom-select"
                                          placeholder={t("select")}
                                          noOptionsMessage={() =>
                                            t("noOptionsAvailable")
                                          }
                                        />
                                        {error && (
                                          <div className="invalid-feedback d-block">
                                            {error.message}
                                          </div>
                                        )}
                                      </>
                                    );
                                  }}
                                />
                              </Form.Group>
                            </Col>

                            <Col md="4">
                              <Form.Group>
                                <Form.Label className="data-font-size data-font-colour mb-2">
                                  {t("reference")}
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  size="md"
                                  {...register("reference")}
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>

                      {/* ── STEP 2: Request & Schedule ── */}
                      <Accordion.Item eventKey="2" className="rf-accordion-item">
                        <Accordion.Header>
                          <span className="h5 fw-medium mb-0 d-flex align-items-center data-font-colour">
                            <span className="rf-header-icon bg-address">
                              <FontAwesomeIcon icon={faCalendarAlt} />
                            </span>
                            <span className="rf-header-text">
                              <span>{t("requestAndSchedule")}</span>
                              <span className="rf-header-subtitle">
                                {t("requestScheduleSub")}
                              </span>
                            </span>
                          </span>
                        </Accordion.Header>
                        <Accordion.Body>
                          <Row className="mb-3">
                            <Col md="3">
                              <Form.Group>
                                <Form.Label className="data-font-size data-font-colour mb-2">
                                  {t("requestDate")}
                                  <span className="text-danger">*</span>
                                </Form.Label>
                                <Controller
                                  control={control}
                                  name="date"
                                  render={({ field }) => (
                                    <div className={`date-picker-wrapper position-relative ${errors.date ? "is-invalid-wrapper" : ""}`}>
                                      <DatePicker
                                        selected={field.value}
                                        onChange={(selectedDate) => {
                                          field.onChange(selectedDate);
                                          setIsDateOpen(false);
                                          setDate(selectedDate);
                                          trigger("dueDate");
                                        }}
                                        open={isDateOpen}
                                        onInputClick={() => setIsDateOpen(true)}
                                        onClickOutside={() => setIsDateOpen(false)}
                                        dateFormat="dd-MM-yyyy"
                                        className={`form-control pe-4 ${
                                          errors.date ? "is-invalid" : ""
                                        }`}
                                        placeholderText="dd-mm-yyyy"
                                      />
                                      <span
                                        className="date-picker-calendar-icon position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 lh-1 cursor-pointer z-index-1"
                                        onClick={() => setIsDateOpen((prev) => !prev)}
                                      >
                                        <FontAwesomeIcon icon={faCalendarAlt} className="text-muted" />
                                      </span>
                                    </div>
                                  )}
                                />
                                {errors.date && (
                                  <Form.Control.Feedback
                                    type="invalid"
                                    className="d-block"
                                  >
                                    {errors.date.message}
                                  </Form.Control.Feedback>
                                )}
                              </Form.Group>
                            </Col>

                            <Col md="3">
                              <Form.Group>
                                <Form.Label className="data-font-size data-font-colour mb-2">
                                  {t("expirationDate")}
                                  <span className="text-danger">*</span>
                                </Form.Label>
                                <Controller
                                  control={control}
                                  name="dueDate"
                                  render={({ field }) => (
                                    <div className={`date-picker-wrapper position-relative ${errors.dueDate ? "is-invalid-wrapper" : ""}`}>
                                      <DatePicker
                                        selected={field.value}
                                        onChange={(selectedDate) => {
                                          field.onChange(selectedDate);
                                          setIsDueDateOpen(false);
                                          setDueDate(selectedDate);
                                        }}
                                        open={isDueDateOpen}
                                        onInputClick={() => setIsDueDateOpen(true)}
                                        onClickOutside={() => setIsDueDateOpen(false)}
                                        dateFormat="dd-MM-yyyy"
                                        className={`form-control pe-4 ${
                                          errors.dueDate ? "is-invalid" : ""
                                        }`}
                                        placeholderText="dd-mm-yyyy"
                                      />
                                      <span
                                        className="date-picker-calendar-icon position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 lh-1 cursor-pointer z-index-1"
                                        onClick={() => setIsDueDateOpen((prev) => !prev)}
                                      >
                                        <FontAwesomeIcon icon={faCalendarAlt} className="text-muted" />
                                      </span>
                                    </div>
                                  )}
                                />
                                {errors.dueDate && (
                                  <Form.Control.Feedback
                                    type="invalid"
                                    className="d-block"
                                  >
                                    {errors.dueDate.message}
                                  </Form.Control.Feedback>
                                )}
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col md="3">
                              <Form.Group>
                                <Form.Label className="data-font-size data-font-colour mb-2">
                                  {t("startDate")}
                                  <span className="text-danger">*</span>
                                </Form.Label>
                                <Controller
                                  control={control}
                                  name="startDate"
                                  render={({ field }) => (
                                    <div className={`date-picker-wrapper position-relative ${errors.startDate ? "is-invalid-wrapper" : ""}`}>
                                      <DatePicker
                                        selected={field.value}
                                        onChange={(date) => {
                                          field.onChange(date);
                                          setIsStartDateOpen(false);
                                        }}
                                        open={isStartDateOpen}
                                        onInputClick={() => setIsStartDateOpen(true)}
                                        onClickOutside={() => setIsStartDateOpen(false)}
                                        dateFormat="dd-MM-yyyy"
                                        className={`form-control pe-4 ${
                                          errors.startDate ? "is-invalid" : ""
                                        }`}
                                        placeholderText="dd-mm-yyyy"
                                      />
                                      <span
                                        className="date-picker-calendar-icon position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 lh-1 cursor-pointer z-index-1"
                                        onClick={() => setIsStartDateOpen((prev) => !prev)}
                                      >
                                        <FontAwesomeIcon icon={faCalendarAlt} className="text-muted" />
                                      </span>
                                    </div>
                                  )}
                                />
                                <Form.Control.Feedback
                                  type="invalid"
                                  className="d-block"
                                >
                                  {errors.startDate?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col md="3">
                              <Form.Group>
                                <Form.Label className="data-font-size data-font-colour mb-2">
                                  {t("endDate")}
                                </Form.Label>
                                <Controller
                                  control={control}
                                  name="endDate"
                                  render={({ field }) => (
                                    <div className={`date-picker-wrapper position-relative ${errors.endDate ? "is-invalid-wrapper" : ""}`}>
                                      <DatePicker
                                        selected={field.value}
                                        onChange={(date) => {
                                          field.onChange(date);
                                          setIsEndDateOpen(false);
                                        }}
                                        open={isEndDateOpen}
                                        onInputClick={() => setIsEndDateOpen(true)}
                                        onClickOutside={() => setIsEndDateOpen(false)}
                                        dateFormat="dd-MM-yyyy"
                                        className={`form-control pe-4 ${
                                          errors.endDate ? "is-invalid" : ""
                                        }`}
                                        placeholderText="dd-mm-yyyy"
                                      />
                                      <span
                                        className="date-picker-calendar-icon position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 lh-1 cursor-pointer z-index-1"
                                        onClick={() => setIsEndDateOpen((prev) => !prev)}
                                      >
                                        <FontAwesomeIcon icon={faCalendarAlt} className="text-muted" />
                                      </span>
                                    </div>
                                  )}
                                />
                                <Form.Control.Feedback
                                  type="invalid"
                                  className="d-block"
                                >
                                  {errors.endDate?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col md="2">
                              <Form.Group>
                                <Form.Label className="data-font-size data-font-colour mb-2">
                                  {t("startingTime")}
                                </Form.Label>
                                <Controller
                                  control={control}
                                  name="startTime"
                                  render={({ field }) => (
                                    <DatePicker
                                      selected={field.value}
                                      onChange={field.onChange}
                                      showTimeSelect
                                      showTimeSelectOnly
                                      timeIntervals={15}
                                      timeCaption="Time"
                                      dateFormat="HH:mm"
                                      timeFormat="HH:mm"
                                      className={`form-control ${
                                        errors.startTime ? "is-invalid" : ""
                                      }`}
                                      placeholderText="HH:mm"
                                      customInput={<TimeInput />}
                                    />
                                  )}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.startTime?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>

                      {/* ── STEP 3: Pricing & Frequency ── */}
                      <Accordion.Item eventKey="3" className="rf-accordion-item">
                        <Accordion.Header>
                          <span className="h5 fw-medium mb-0 d-flex align-items-center data-font-colour">
                            <span className="rf-header-icon bg-invoice">
                              <FontAwesomeIcon icon={faFileInvoiceDollar} />
                            </span>
                            <span className="rf-header-text">
                              <span>{t("pricingAndFrequency")}</span>
                              <span className="rf-header-subtitle">
                                {t("pricingFrequencySub")}
                              </span>
                            </span>
                          </span>
                        </Accordion.Header>
                        <Accordion.Body>
                          <Row className="mb-3">
                            <Col md="4">
                              <Form.Group>
                                <Form.Label className="data-font-size data-font-colour mb-2">
                                  {t("paymentConditions")}
                                  <span className="text-danger">*</span>
                                </Form.Label>
                                <Controller
                                  name="paymentTerms"
                                  control={control}
                                  rules={{
                                    required: t("paymentTermsRequired"),
                                  }}
                                  render={({
                                    field,
                                    fieldState: { error },
                                  }) => {
                                    const options = [
                                      {
                                        value: "Payment Request",
                                        label: t("paymentRequest"),
                                      },
                                      { value: "Cash", label: t("cash") },
                                      {
                                        value: "Bank Transfer",
                                        label: t("bankTransfer"),
                                      },
                                    ];
                                    return (
                                      <>
                                        <Select
                                          key={i18n.language}
                                          options={options}
                                          isClearable
                                          {...field}
                                          value={
                                            options.find(
                                              (opt) => opt.value === field.value
                                            ) || null
                                          }
                                          onChange={(selected) => {
                                            const selectedValue = selected
                                              ? selected.value
                                              : "";
                                            field.onChange(selectedValue);
                                          }}
                                          onBlur={field.onBlur}
                                          className={`custom-select-container ${
                                            error ? "is-invalid" : ""
                                          }`}
                                          classNamePrefix="custom-select"
                                          placeholder={t("select")}
                                          noOptionsMessage={() =>
                                            t("noOptionsAvailable")
                                          }
                                        />
                                        {error && (
                                          <div className="invalid-feedback d-block">
                                            {error.message}
                                          </div>
                                        )}
                                      </>
                                    );
                                  }}
                                />
                              </Form.Group>
                            </Col>

                            <Col md="4">
                              <Form.Group>
                                <Form.Label className="data-font-size data-font-colour mb-2">
                                  {t("invoiceType")}
                                  <span className="text-danger">*</span>
                                </Form.Label>
                                <Controller
                                  name="invoiceType"
                                  control={control}
                                  rules={{
                                    required: t("invoiceTypeRequired"),
                                  }}
                                  render={({
                                    field,
                                    fieldState: { error },
                                  }) => {
                                    const options = [
                                      {
                                        value: "Invoice",
                                        label: t("invoice"),
                                      },
                                      {
                                        value: "Proforma Invoice",
                                        label: t("proforma"),
                                      },
                                    ];
                                    return (
                                      <>
                                        <Select
                                          {...field}
                                          options={options}
                                          isClearable
                                          value={
                                            options.find(
                                              (opt) => opt.value === field.value
                                            ) || null
                                          }
                                          onChange={(selected) => {
                                            const value = selected
                                              ? selected.value
                                              : "";
                                            field.onChange(value);
                                          }}
                                          onBlur={field.onBlur}
                                          className={`custom-select-container ${
                                            error ? "is-invalid" : ""
                                          }`}
                                          classNamePrefix="custom-select"
                                          placeholder={t("select")}
                                          noOptionsMessage={() =>
                                            t("noOptionsAvailable")
                                          }
                                          menuPortalTarget={document.body}
                                          menuPosition="fixed"
                                        />
                                        {error && (
                                          <div className="invalid-feedback d-block">
                                            {error.message}
                                          </div>
                                        )}
                                      </>
                                    );
                                  }}
                                />
                              </Form.Group>
                            </Col>

                            <Col md="4">
                              <Form.Group>
                                <Form.Label className="data-font-size data-font-colour mb-2">
                                  {t("billingCycle")}
                                </Form.Label>
                                <Controller
                                  name="billingCycle"
                                  control={control}
                                  render={({
                                    field,
                                    fieldState: { error },
                                  }) => {
                                    const options = [
                                      {
                                        value: "OneTime",
                                        label: t("billingCycleLabel.OneTime"),
                                      },
                                      {
                                        value: "Monthly",
                                        label: t("billingCycleLabel.Monthly"),
                                      },
                                      {
                                        value: "Yearly",
                                        label: t("billingCycleLabel.Yearly"),
                                      },
                                    ];
                                    return (
                                      <>
                                        <Select
                                          key={i18n.language}
                                          options={options}
                                          isClearable
                                          {...field}
                                          value={
                                            options.find(
                                              (opt) => opt.value === field.value
                                            ) || null
                                          }
                                          onChange={(selected) =>
                                            field.onChange(
                                              selected ? selected.value : ""
                                            )
                                          }
                                          onBlur={field.onBlur}
                                          className={`custom-select-container ${
                                            error ? "is-invalid" : ""
                                          }`}
                                          classNamePrefix="custom-select"
                                          placeholder={t("select")}
                                          noOptionsMessage={() =>
                                            t("noOptionsAvailable")
                                          }
                                        />
                                        {error && (
                                          <div className="invalid-feedback d-block">
                                            {error.message}
                                          </div>
                                        )}
                                      </>
                                    );
                                  }}
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col md="3">
                              <Form.Group>
                                <Form.Label className="data-font-size data-font-colour mb-2">
                                  {t("proposalCost")}
                                </Form.Label>
                                <InputGroup>
                                  <InputGroup.Text>
                                    {currencySymbol}
                                  </InputGroup.Text>
                                  <Form.Control
                                    type="text"
                                    inputMode="decimal"
                                    {...register("cost", {
                                      required: false,
                                      pattern: {
                                        value: new RegExp(
                                          `^\\d{1,8}([\\${decimalSeparator}]\\d{1,2})?$`
                                        ),
                                        message: t("invalidRate"),
                                      },
                                      setValueAs: (value) => {
                                        if (
                                          value === null ||
                                          value === undefined ||
                                          value === ""
                                        )
                                          return "";
                                        const normalizedValue = String(
                                          value
                                        ).replace(decimalSeparator, ".");
                                        const parsed = parseFloat(
                                          normalizedValue
                                        );
                                        return isNaN(parsed) ? "" : parsed;
                                      },
                                    })}
                                    isInvalid={!!errors.cost}
                                    placeholder={costPlaceholder}
                                    onKeyDown={(e) => {
                                      const allowedKeys = [
                                        "Backspace",
                                        "Delete",
                                        "ArrowLeft",
                                        "ArrowRight",
                                        "Tab",
                                      ];
                                      const isNumber = /^[0-9]$/.test(e.key);
                                      const isDecimalSeparator =
                                        e.key === decimalSeparator;
                                      const value = e.currentTarget.value;
                                      if (
                                        !isNumber &&
                                        !allowedKeys.includes(e.key) &&
                                        !(
                                          isDecimalSeparator &&
                                          !value.includes(decimalSeparator)
                                        )
                                      ) {
                                        e.preventDefault();
                                      }
                                    }}
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {errors.cost?.message}
                                  </Form.Control.Feedback>
                                </InputGroup>
                              </Form.Group>
                            </Col>

                            {/* Job Frequency */}
                            <Col md="auto" className="offset-md-1">
                              <Form.Group className="d-flex flex-column align-items-start">
                                <Form.Label className="data-font-size data-font-colour mb-2 d-block">
                                  {t("jobFrequency")}
                                </Form.Label>
                                <InputGroup className="job-frequency-group d-flex w-auto border rounded">
                                  <div className="job-frequency-count-wrapper border-end position-relative z-1">
                                    <Form.Control
                                      type="number"
                                      min="1"
                                      max="31"
                                      placeholder="1x"
                                      className="job-frequency-count"
                                      {...register("jobFrequencyCount", {
                                        min: 1,
                                        max: 31,
                                        setValueAs: (v) =>
                                          v === "" || v == null
                                            ? null
                                            : parseInt(v, 10),
                                      })}
                                      isInvalid={!!errors.jobFrequencyCount}
                                    />
                                    {watchedJobFrequencyCount !== null &&
                                      watchedJobFrequencyCount !== undefined &&
                                      watchedJobFrequencyCount !== "" && (
                                        <span
                                          className={`job-frequency-x-suffix ${
                                            String(watchedJobFrequencyCount)
                                              .length === 2
                                              ? "job-frequency-x-suffix--offset-2"
                                              : "job-frequency-x-suffix--offset-1"
                                          }`}
                                        >
                                          x
                                        </span>
                                      )}
                                  </div>

                                  <Controller
                                    name="jobFrequency"
                                    control={control}
                                    render={({
                                      field,
                                      fieldState: { error },
                                    }) => {
                                      const formatLabel = (text) =>
                                        text ? text.toLowerCase() : "";

                                      const options = [
                                        {
                                          value: "OneTime",
                                          label: formatLabel(
                                            t("jobFrequencyLabel.OneTime")
                                          ),
                                        },
                                        {
                                          value: "PerDay",
                                          label: formatLabel(
                                            t("jobFrequencyLabel.PerDay")
                                          ),
                                        },
                                        {
                                          value: "PerWeek",
                                          label: formatLabel(
                                            t("jobFrequencyLabel.PerWeek")
                                          ),
                                        },
                                        {
                                          value: "PerMonth",
                                          label: formatLabel(
                                            t("jobFrequencyLabel.PerMonth")
                                          ),
                                        },
                                        {
                                          value: "PerYear",
                                          label: formatLabel(
                                            t("jobFrequencyLabel.PerYear")
                                          ),
                                        },
                                      ];

                                      return (
                                        <div className="job-frequency-select-wrapper">
                                          <Select
                                            key={i18n.language}
                                            options={options}
                                            isClearable
                                            {...field}
                                            value={
                                              options.find(
                                                (opt) =>
                                                  opt.value === field.value
                                              ) || null
                                            }
                                            onChange={(selected) =>
                                              field.onChange(
                                                selected ? selected.value : ""
                                              )
                                            }
                                            onBlur={field.onBlur}
                                            className={`custom-select-container ${
                                              error ? "is-invalid" : ""
                                            }`}
                                            classNamePrefix="custom-select"
                                            placeholder={t("select")}
                                            isSearchable={false}
                                            noOptionsMessage={() =>
                                              t("noOptionsAvailable")
                                            }
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                          />
                                          {error && (
                                            <div className="invalid-feedback d-block">
                                              {error.message}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    }}
                                  />
                                </InputGroup>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>

                      {/* ── STEP 4: Documents & Comments ── */}
                      <Accordion.Item eventKey="4" className="rf-accordion-item">
                        <Accordion.Header>
                          <span className="h5 fw-medium mb-0 d-flex align-items-center data-font-colour">
                            <span className="rf-header-icon bg-contact">
                              <FontAwesomeIcon icon={faFolderOpen} />
                            </span>
                            <span className="rf-header-text">
                              <span>{t("documentsAndComments")}</span>
                              <span className="rf-header-subtitle">
                                {t("documentsCommentsSub")}
                              </span>
                            </span>
                          </span>
                        </Accordion.Header>
                        <Accordion.Body>
                          <Row className="mb-3">
                            <Col md="4">
                              <Form.Label className="data-font-size data-font-colour mb-2">
                                {t("uploadDocuments")}
                              </Form.Label>
                              <div
                                role="button"
                                tabIndex={0}
                                className={`d-flex align-items-center gap-2 border rounded-2 px-3 py-2 cursor-pointer bg-light ${
                                  isDragActive
                                    ? "border-primary bg-primary-subtle"
                                    : ""
                                }`}
                                onClick={() => fileInputRef.current?.click()}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    fileInputRef.current?.click();
                                  }
                                }}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                              >
                                <span className="d-flex align-items-center gap-2 border rounded-2 px-2 py-1 bg-white fw-medium font-size-13 text-muted flex-shrink-0">
                                  <i className="fas fa-upload font-size-11" />{" "}
                                  {t("chooseFiles")}
                                </span>
                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  onClick={(e) => e.stopPropagation()}
                                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                                  multiple
                                  className="d-none"
                                  onChange={(e) => {
                                    if (
                                      !e.target.files ||
                                      e.target.files.length === 0
                                    )
                                      return;
                                    handleFilesSelected(e.target.files);
                                    e.target.value = "";
                                  }}
                                />
                              </div>
                            </Col>

                            <Col md="8">
                              {(uploadedFiles.length > 0 ||
                                savedAttachments.length > 0 ||
                                attachmentsLoading) && (
                                <>
                                  <Form.Label className="data-font-size data-font-colour mb-2 d-none d-md-block">
                                    &nbsp;
                                  </Form.Label>
                                  {attachmentsLoading ? (
                                    <Spinner
                                      animation="border"
                                      size="sm"
                                      className="mt-2"
                                    />
                                  ) : (
                                    <div className="d-flex flex-wrap gap-2 align-items-center">
                                      {uploadedFiles.map((file, idx) => (
                                        <div
                                          key={idx}
                                          className="d-inline-flex align-items-center rounded-pill bg-secondary-subtle px-3 py-1 mw-100"
                                        >
                                          <i className="fas fa-file me-2 text-primary font-size-12" />
                                          <span className="flex-grow-1 text-truncate min-w-0 fs-11">
                                            {file.name}
                                          </span>
                                          <button
                                            type="button"
                                            role="button"
                                            tabIndex={0}
                                            className="btn btn-link p-0 ms-2 text-muted font-size-12 flex-shrink-0"
                                            onClick={() =>
                                              setUploadedFiles((prev) =>
                                                prev.filter(
                                                  (_, i) => i !== idx
                                                )
                                              )
                                            }
                                            aria-label={`Remove ${file.name}`}
                                          >
                                            <i className="fas fa-times" />
                                          </button>
                                        </div>
                                      ))}

                                      {savedAttachments.map((att) => (
                                        <div
                                          key={att.AttachmentId}
                                          className="d-inline-flex align-items-center rounded-pill bg-secondary-subtle px-3 py-1 mw-100"
                                        >
                                          <i className="fas fa-file me-2 text-primary font-size-12" />
                                          <span className="flex-grow-1 font-size-13 text-truncate min-w-0 fs-11">
                                            {att.OriginalName}
                                          </span>
                                          <div className="d-flex gap-1 flex-shrink-0 ms-2">
                                            <button
                                              type="button"
                                              className="btn btn-link p-0 text-primary font-size-12"
                                              onClick={() =>
                                                handleViewAttachment(att)
                                              }
                                              aria-label={`View ${att.OriginalName}`}
                                            >
                                              <i className="fas fa-eye" />
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-link p-0 text-muted font-size-12"
                                              onClick={() =>
                                                handleDeleteSavedAttachment(
                                                  att.AttachmentId
                                                )
                                              }
                                              aria-label={`Remove ${att.OriginalName}`}
                                            >
                                              <i className="fas fa-times" />
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </>
                              )}
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col md="9">
                              <Form.Group>
                                <Form.Label className="data-font-size data-font-colour mb-2">
                                  {t("proposalComment")}
                                </Form.Label>
                                <Controller
                                  control={control}
                                  name="proposalComment"
                                  render={({ field }) => (
                                    <Form.Control
                                      {...field}
                                      as="textarea"
                                      rows={3}
                                      placeholder={t("commentPlaceholder")}
                                      className={`${
                                        errors.comment ? "is-invalid" : ""
                                      }`}
                                    />
                                  )}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.comment?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>

                    <Card className="mt-2">
                      <Card.Body>
                        <Row className="justify-content-between align-items-center">
                          <Col xs="auto">
                            <Button
                              variant="falcon-default"
                              size="sm"
                              type="button"
                              onClick={() => handleDiscard()}
                            >
                              {t("cancel")}
                            </Button>
                          </Col>
                          <Col xs="auto">
                            {currentStep > 1 && (
                              <Button
                                variant="falcon-default"
                                size="sm"
                                className="me-3"
                                type="button"
                                onClick={(e) => handleProposalBack(e)}
                              >
                                {t("back")}
                              </Button>
                            )}
                            {currentStep < 4 ? (
                              <Button
                                key="btn-step-next"
                                variant="primary"
                                size="sm"
                                type="button"
                                onClick={(e) => handleProposalNext(e)}
                              >
                                {t("next")}{" "}
                                <FontAwesomeIcon
                                  icon={faArrowRight}
                                  className="ms-1"
                                />
                              </Button>
                            ) : (
                              <Button
                                key="btn-step-save"
                                variant="primary"
                                size="sm"
                                type="button"
                                onClick={(e) => handleProposalSave(e)}
                                disabled={isLoading}
                              >
                                {isLoading ? (
                                  t("saving")
                                ) : (
                                  <>
                                    {t("save")}{" "}
                                    <FontAwesomeIcon
                                      icon={faArrowRight}
                                      className="ms-1"
                                    />
                                  </>
                                )}
                              </Button>
                            )}
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Form>
            </>
          ) : (
            <>
              {isTaskLoading ? (
                <div className="text-center my-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">{t("loadingTasks")}</span>
                  </Spinner>
                </div>
              ) : (
                <Row className="g-3 mb-3">
                  <Col lg={12} xxl={12}>
                    <Card className="mb-3 h-100">
                      <Card.Body className="position-relative pe-4 pb-2">
                        <Row className="flex-between-center">
                          <Col md>
                            <h5 className="mb-2 mb-md-0">{t("tasks")}</h5>
                          </Col>
                          <Col xs="auto">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => setShowTaskModal(true)}
                            >
                              {t("newTaskSingular")}
                            </Button>
                          </Col>
                        </Row>
                        <Row>
                          {taskWithSubtasks.length === 0 ? (
                            <div className="text-muted text-center mt-3">
                              {t("noTasksAvailable")}
                            </div>
                          ) : (
                            taskWithSubtasks.map((task, i) => (
                              <Card
                                key={task.ProposalTaskId}
                                className="mb-3 no-shadow"
                              >
                                <Card.Header>
                                  <div className="d-flex align-items-center gap-2">
                                    <h5 className="fw-bold mb-0">
                                      {i + 1}. {task.label}{" "}
                                      {task.recurrence.frequency.toLowerCase() === "onetime" ? (
                                        <>({t("frequencyLabel.onetime")})</>
                                      ) : (
                                        <>
                                          ({getOccurrenceCount(task.recurrence)}x{" "}
                                          {t(
                                            `frequencyLabel.${task.recurrence.frequency.toLowerCase()}`
                                          )}
                                          )
                                        </>
                                      )}
                                    </h5>

                                    <IconButton
                                      variant="outline-danger"
                                      size="sm"
                                      icon="trash-alt"
                                      onClick={() =>
                                        handleDeleteTask(task.ProposalTaskId)
                                      }
                                    />
                                  </div>
                                </Card.Header>

                                <Card.Body className="p-0">
                                  <Table
                                    id="taskTable"
                                    bordered
                                    hover
                                    responsive
                                    size="sm"
                                  >
                                    <thead>
                                      <tr>
                                        <th className="bg-primary-subtle task-column-size">
                                          {t("subTasks")}
                                        </th>

                                        {task.recurrence.frequency.toLowerCase() === "daily" ||
                                          task.recurrence.frequency.toLowerCase() === "weekly" ? (
                                          [
                                            "monday",
                                            "tuesday",
                                            "wednesday",
                                            "thursday",
                                            "friday",
                                            "saturday",
                                            "sunday",
                                          ].map((day, idx) => (
                                            <th
                                              key={idx}
                                              className="text-center bg-primary-subtle recurrence-column"
                                            >
                                              {t(`days.${day}`)}
                                            </th>
                                          ))
                                        ) : task.recurrence.frequency.toLowerCase() === "monthly" || task.recurrence.frequency.toLowerCase() === "yearly" ? (
                                          moment.months().map((month, idx) => (
                                            <th
                                              key={idx}
                                              className="text-center bg-primary-subtle recurrence-column"
                                            >
                                              {t(`shortMonths.${month.toLowerCase()}`)}
                                            </th>
                                          ))
                                        ) : null}
                                      </tr>
                                    </thead>

                                    <tbody>
                                      {task.Subtasks?.map((sub) => {
                                        const isChecked = selectedSubtasksMap[task.ProposalTaskId]?.has(
                                          sub.SubtaskId
                                        );

                                        return (
                                          <tr key={sub.SubtaskId} className="m-0">
                                            <td className="task-column-size">
                                              <Form.Check
                                                type="checkbox"
                                                className="mb-0 pb-0"
                                                id={`subtask-${task.ProposalTaskId}-${sub.SubtaskId}`}
                                                label={sub.Subtask}
                                                defaultChecked
                                                checked={isChecked}
                                                onChange={(e) => {
                                                  setSelectedSubtasksMap((prev) => {
                                                    const newMap = { ...prev };
                                                    const set = new Set(prev[task.ProposalTaskId] || []);
                                                    if (e.target.checked) {
                                                      set.add(sub.SubtaskId);
                                                    } else {
                                                      set.delete(sub.SubtaskId);
                                                    }
                                                    newMap[task.ProposalTaskId] = set;
                                                    return newMap;
                                                  });
                                                }}
                                              />
                                            </td>

                                            {task.recurrence.frequency.toLowerCase() !== "onetime" && (
                                              <RecurrenceColumns recurrence={task.recurrence} />
                                            )}
                                          </tr>
                                        );
                                      })}
                                      <tr>
                                        <td colSpan={task.recurrence.frequency.toLowerCase() === "daily" || task.recurrence.frequency.toLowerCase() === "weekly" ? 8 : task.recurrence.frequency.toLowerCase() === "monthly" || task.recurrence.frequency.toLowerCase() === "yearly" ? 13 : 1}>
                                          {!addingSubtaskMap[task.ProposalTaskId] ? (
                                            <Button
                                              variant="link"
                                              size="sm"
                                              className="p-0 text-decoration-none"
                                              onClick={() => openAddSubtask(task.ProposalTaskId)}
                                            >
                                              <i className="fas fa-plus me-1" />
                                              {t("addSubtask")}
                                            </Button>
                                          ) : (
                                            <div className="d-flex align-items-center gap-2">
                                              <Form.Control
                                                ref={(el) => (subtaskInputRefs.current[task.ProposalTaskId] = el)}
                                                value={newSubtaskName[task.ProposalTaskId] || ""}
                                                placeholder={t("addNewSubtask") || "Enter sub-task name..."}
                                                aria-label={t("addSubtask")}
                                                size="sm"
                                                className="rounded-3 border-primary-subtle subtask-input-group"
                                                onChange={(e) => setNewSubtaskName((previous) => ({ ...previous, [task.ProposalTaskId]: e.target.value }))}
                                                onKeyDown={(e) => {
                                                  if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    handleAddCustomSubtaskAndContinue(task.ProposalTaskId);
                                                  }
                                                  if (e.key === "Escape") {
                                                    closeAddSubtask(task.ProposalTaskId);
                                                  }
                                                }}
                                                onBlur={() => {
                                                  const val = (newSubtaskName[task.ProposalTaskId] || "").trim();
                                                  if (!val) closeAddSubtask(task.ProposalTaskId);
                                                }}
                                              />
                                              <OverlayTrigger overlay={<Tooltip id={`add-subtask-tooltip-${task.ProposalTaskId}`}>{t("addSubtask")}</Tooltip>}>
                                                <Button
                                                  variant="link"
                                                  size="sm"
                                                  className="rounded-2 bg-primary-subtle border border-primary-subtle text-primary"
                                                  onMouseDown={(e) => e.preventDefault()}
                                                  onClick={() => handleAddCustomSubtaskAndContinue(task.ProposalTaskId)}
                                                >
                                                  <i className="fas fa-arrow-up" />
                                                </Button>
                                              </OverlayTrigger>
                                              <OverlayTrigger overlay={<Tooltip id={`cancel-subtask-tooltip-${task.ProposalTaskId}`}>{t("cancel")}</Tooltip>}>
                                                <Button
                                                  variant="link"
                                                  size="sm"
                                                  className="rounded-2 bg-light border border-secondary-subtle text-secondary"
                                                  onMouseDown={(e) => e.preventDefault()}
                                                  onClick={() => closeAddSubtask(task.ProposalTaskId)}
                                                >
                                                  <i className="fas fa-times" />
                                                </Button>
                                              </OverlayTrigger>
                                            </div>
                                          )}
                                        </td>
                                      </tr>
                                    </tbody>
                                  </Table>
                                </Card.Body>
                              </Card>
                            ))
                          )}
                        </Row>
                      </Card.Body>
                      <Divider></Divider>
                      <Card.Body className="pt-0">
                        <Row className="justify-content-end">
                          <Col xs="auto">
                            <Button
                              variant="falcon-default"
                              size="sm"
                              className="me-3"
                              type="button"
                              onClick={() => {
                                setShowTaskForm(false);

                                const currentValues = getValues(); // get latest form values

                                setValue(
                                  "reference",
                                  currentValues.reference || ""
                                );
                                setValue("status", currentValues.status || "");
                                setValue("client", currentValues.client || "");
                                setValue(
                                  "contactperson",
                                  currentValues.contactperson || ""
                                );
                                setValue(
                                  "location",
                                  currentValues.location || ""
                                );

                                const date = currentValues.date
                                  ? new Date(currentValues.date)
                                  : null;
                                const dueDate = currentValues.dueDate
                                  ? new Date(currentValues.dueDate)
                                  : null;
                                const startDate = currentValues.startDate
                                  ? new Date(currentValues.startDate)
                                  : null;
                                const endDate = currentValues.endDate
                                  ? new Date(currentValues.endDate)
                                  : null;

                                setValue("date", date);
                                setDate(date);

                                setValue("dueDate", dueDate);
                                setDueDate(dueDate);

                                setValue(
                                  "paymentTerms",
                                  currentValues.paymentTerms || ""
                                );
                                setValue(
                                  "invoiceType",
                                  currentValues.invoiceType || ""
                                );
                                setValue(
                                  "workType",
                                  currentValues.workType || ""
                                );

                                setValue("startDate", startDate);
                                if (endDate) setValue("endDate", endDate);

                                setValue("cost", currentValues.cost || "");
                                setValue("jobFrequency", currentValues.jobFrequency || "");
                                setValue("jobFrequencyCount", currentValues.jobFrequencyCount ?? "");
                                setValue("billingCycle", currentValues.billingCycle ?? "");

                                setValue("clientSignatureRequired", currentValues.clientSignatureRequired ?? false);
                                setValue("pauseRequired", currentValues.pauseRequired ?? false);
                              }}
                            >
                              {t("back")}
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              className="ms-2"
                              onClick={() => {
                                if (taskWithSubtasks.length === 0) {
                                  toast.error(t("pleaseAddTask"), {
                                    position: "top-right",
                                    autoClose: 3000,
                                  });
                                  return;
                                }

                                handleCreateDocument(selectedWorkType);
                              }}
                            >
                              {t("design")} <i className="fas fa-cog"></i>
                            </Button>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}
            </>
          )}
        </>
      )}
      <TaskModal
        show={showTaskModal}
        handleClose={() => setShowTaskModal(false)}
        onSaveSelected={handleSaveSelectedTasks}
        proposalId={proposalId} // Pass the ID here
        currentVersionId={currentVersionId}
        defaultJobFrequency={watchedJobFrequency}
        defaultJobFrequencyCount={getValues("jobFrequencyCount")}
        proposalStartDate={watchedStartDate}
        setTaskWithSubtasks={setTaskWithSubtasks}
        setEnrichedTasks={setEnrichedTasks}
        setSelectedSubtasksMap={setSelectedSubtasksMap}
      />
      {selectedFormData && (
        <ProposalModal
          show={showModal}
          handleClose={handleClose}
          selectedTemplate={selectedFormData.templateName}
          formData={selectedFormData}
        />
      )}
    <Modal
      show={showClientChangeModal}
      onHide={() => {
        setShowClientChangeModal(false);
        setPendingClientChange(null);
      }}
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title>{t("changeProposalClient")}</Modal.Title>
        <Button
          variant="close"
          onClick={() => {
            setShowClientChangeModal(false);
            setPendingClientChange(null);
          }}
        />
      </Modal.Header>

      <Modal.Body>
          <p className="mb-3">
            {t("changeClientWarningOrders", {
              currentClient: clientName,
              newClient: pendingClientName,
            })}
          </p>
          <p className="mb-0">
            {t("confirmQuestion")}
          </p>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="falcon-default"
          onClick={() => {
            setShowClientChangeModal(false);
            setPendingClientChange(null);
          }}
        >
          {t("cancel")}
        </Button>

        <Button
          variant="primary"
          onClick={() => {
            if (pendingClientChange) {
              const { field, selectedValue } = pendingClientChange;
              field.onChange(selectedValue);
              setSelectedClient(selectedValue);
              setValue("client", selectedValue, { shouldValidate: true });
            }
            setShowClientChangeModal(false);
            setPendingClientChange(null);
          }}
        >
        {t("confirmChange")}
        </Button>
      </Modal.Footer>
    </Modal>
    </FormProvider>
  );
};

export default EditProposal